import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Pencil, Trash2, ChevronRight, FolderOpen, Image as ImageIcon, Wand2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { neonQuery, callClaude } from '../services/api';
import MediaPicker from '../components/MediaPicker';
import logoCpSvg from '../logo-cp.svg';

// ─── Inline Editable Text ────────────────────────────────────
function Editable({ value, onChange, tag: Tag = 'span', style = {}, isHtml = false, className }) {
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);
  const hoverStyle = { cursor: 'pointer', outline: editing ? '2px solid #DC582A' : 'none', borderRadius: 3, transition: 'outline 0.15s', position: 'relative', minWidth: 20 };
  const handleBlur = () => {
    setEditing(false);
    const el = ref.current;
    if (!el) return;
    const newVal = isHtml ? el.innerHTML : el.textContent;
    if (newVal !== value) onChange(newVal);
  };
  if (isHtml) {
    return <Tag ref={ref} className={className} style={{ ...style, ...hoverStyle }} contentEditable suppressContentEditableWarning
      dangerouslySetInnerHTML={{ __html: value }} onFocus={() => setEditing(true)} onBlur={handleBlur}
      title="Click to edit" />;
  }
  return <Tag ref={ref} style={{ ...style, ...hoverStyle }} contentEditable suppressContentEditableWarning
    onFocus={() => setEditing(true)} onBlur={handleBlur} title="Click to edit">{value}</Tag>;
}

// ─── System Prompt (base) ───────────────────────────────────
const BASE_SYSTEM_PROMPT = `You are CrabPlace Marketing Studio — an AI marketing assistant for CrabPlace.com, the first online seafood retailer (est. 1995), based in Maryland.

BRAND GUIDE:
- Colors: Navy #00395C (primary), Orange #DC582A (accent), Light Orange #EA733D, Peach #FCD299
- Font: Montserrat (all weights)
- Headlines: Bold, Navy or White on dark backgrounds
- CTAs: Orange buttons with white text
- Taglines: "We Share the Best Catches with You" / "Our Boxes Make You Happy"
- Brand Values: We Only Deliver the Best · We Know Who We Are · We Bring People Together
- Textures: Waves, Wood, Netting, Burlap, Ocean Floor
- Voice: Warm, confident, direct — like a trusted local seafood expert. Proud Maryland heritage — never generic or corporate. Celebration-forward — crabs are about gatherings and good times. Never clinical, never salesy, never formal.

PRODUCT CATEGORIES:
- Live Maryland Blue Crabs (flagship — seasonal, summer peak)
- Crab Cakes (year-round bestseller)
- Crab Meat (lump, jumbo lump, claw, backfin)
- Shrimp (wild-caught)
- Lobster Tails
- Seafood Combos / Gift Boxes
- Seasonings (Old Bay-style house blend)
- Merchandise / Gift Cards

YOUR JOB:
When the user describes what they want (an email campaign, a landing page, a promotion), you generate the content. You must respond ONLY with valid JSON in this exact format — no markdown, no backticks, no preamble:

{
  "type": "email" | "landing_page" | "both" | "conversation",
  "thinking": "Brief explanation of your creative approach (1-2 sentences)",
  "email": {
    "subject": "Email subject line",
    "previewText": "Preview/preheader text (max 90 chars)",
    "headline": "Main headline in the email",
    "heroImage": "Description of what the hero image should show",
    "body": "The email body copy in HTML. Use inline styles only. Use the brand colors. Keep it concise and compelling. Include a clear CTA button styled with background-color:#DC582A, color:white, padding:14px 32px, border-radius:6px, text-decoration:none, font-family:Montserrat,sans-serif, font-weight:700, display:inline-block.",
    "ctaText": "CTA button text",
    "ctaUrl": "https://crabplace.com/relevant-page"
  },
  "landingPage": {
    "headline": "Main page headline",
    "subheadline": "Supporting text",
    "heroImage": "Description of hero image",
    "sections": [
      {
        "type": "text" | "features" | "testimonial" | "cta" | "product_grid",
        "content": "HTML content for this section with inline brand styles"
      }
    ],
    "ctaText": "Primary CTA text",
    "ctaUrl": "https://crabplace.com/relevant-page"
  },
  "conversation": "If you need more info or are responding conversationally, put your message here"
}

RULES:
- CRITICAL: When the user asks for ANY change (centering, color, text edit, layout fix, combining sections, etc.), you MUST return the full updated landingPage or email JSON object with the change already applied. NEVER respond with type "conversation" when the user wants a change — always return the updated content. Do NOT narrate, plan, or describe what you intend to do. Just make the change and return it.
- The "thinking" field should be a past-tense summary of what you changed (e.g. "Combined the two hero sections into one"), NOT a plan of what you intend to do.
- If the user is just chatting or asking a question with no content to change, use type "conversation".
- If they want an email, include the "email" object.
- If they want a landing page, include the "landingPage" object.
- If both, include both.
- Always write in CrabPlace's voice — warm, celebratory, Maryland-proud.
- Subject lines should be compelling and under 60 characters.
- Email body copy should be scannable — short paragraphs, clear hierarchy.
- Landing pages should tell a story and drive to purchase.
- ALWAYS respond with valid JSON only. No markdown fences. No explanation outside the JSON.

MEDIA LIBRARY — use the search_media_library tool for REAL photography:
- The Brand Kit above contains logos, headers, and a few hand-picked graphics. For actual product photos and lifestyle imagery (steamed crabs, crab cakes on plates, families eating, etc.) call the search_media_library tool to find a real image before you finalize the content.
- Use ascending levels of search specificity: start with a short natural-language query (e.g. "families eating crabs outdoor"). Add category/subcategory/tags/minQuality only when the first pass is too broad.
- Prefer quality_score >= 4 for hero placements and email banners; 3+ is fine for inline body images.
- When a search returns images, embed the asset's "url" as the src of an <img> in your generated HTML — do NOT paraphrase the image as a description in "heroImage" text when a real URL is available.
- You may call the tool more than once per turn if you need a hero plus a secondary image. Keep it under ~4 calls total — don't exhaustively browse.
- If the library has nothing that fits, fall back to the Brand Kit or a prose "heroImage" description, and mention in "thinking" what you couldn't find.`;

// Build the full system prompt by injecting brand assets, strategies, and style prefs
function buildSystemPrompt(brandAssets, strategies, stylePrefs) {
  let prompt = BASE_SYSTEM_PROMPT;

  // Brand assets
  if (brandAssets && brandAssets.length > 0) {
    const images = brandAssets.filter(a => a.asset_type === 'image');
    const blocks = brandAssets.filter(a => a.asset_type === 'block');

    prompt += '\n\nBRAND KIT — USE THESE REAL ASSETS:';

    if (images.length > 0) {
      prompt += '\n\nIMAGES (use these actual URLs in your generated HTML — do NOT use placeholder descriptions):';
      images.forEach(img => {
        prompt += `\n- ${img.name}${img.category ? ` [${img.category}]` : ''}: ${img.url}`;
      });
    }

    if (blocks.length > 0) {
      prompt += '\n\nBUILDING BLOCKS (use this exact HTML when the block type is relevant):';
      blocks.forEach(block => {
        prompt += `\n\n--- ${block.name}${block.category ? ` [${block.category}]` : ''} ---\n${block.html}`;
      });
    }
  }

  // Strategies
  if (strategies && strategies.length > 0) {
    prompt += '\n\nCURRENT MARKETING STRATEGIES & CONTEXT (use this knowledge to inform all content you generate):';
    const cats = [...new Set(strategies.map(s => s.category))];
    cats.forEach(cat => {
      prompt += `\n\n[${cat}]`;
      strategies.filter(s => s.category === cat).forEach(s => {
        prompt += `\n- ${s.title}`;
        if (s.detail) prompt += `: ${s.detail}`;
      });
    });
  }

  // Style preferences
  if (stylePrefs && stylePrefs.length > 0) {
    prompt += '\n\nLEARNED STYLE PREFERENCES (apply these to all content — the user has established these through past feedback):';
    stylePrefs.forEach(p => {
      prompt += `\n- ${p.pref}`;
    });
  }

  return prompt;
}

const MODEL = 'claude-sonnet-4-6';
const TOOL_LOOP_MAX = 6;
const SITE_URL = 'https://cpx-prototype.netlify.app';

// Exposed to Claude via tool use. Returns real asset URLs the model can drop
// into <img src> — avoids "imagine a photo of..." placeholders.
const MEDIA_LIBRARY_SEARCH_TOOL = {
  name: 'search_media_library',
  description: "Search The Crab Place's classified library of product and lifestyle photography. Returns asset metadata + public URLs you can embed as <img src>. Prefer this over placeholder image descriptions. Use ascending specificity — start with a short natural-language `query` and only add category/tags/minQuality when the first pass is too broad.",
  input_schema: {
    type: 'object',
    properties: {
      query: { type: 'string', description: 'Natural language, searched across suggested_name / alt_text / notes / tags. Example: "families eating crabs outside"' },
      category: { type: 'string', enum: ['products', 'plated', 'lifestyle', 'ugc', 'process', 'people', 'marketing'], description: 'Restrict to one category. "products" = isolated product shot; "plated" = cooked dish; "lifestyle" = mood/people/setting.' },
      subcategory: { type: 'string', description: 'e.g. "hard-crabs", "crab-cakes", "crab-meat", "setting-outdoor".' },
      tags: { type: 'array', items: { type: 'string' }, description: 'Tags the asset must have (AND). Use 1-3 tags max; more tags → tighter match → fewer rows.' },
      usableFor: { type: 'array', items: { type: 'string', enum: ['hero', 'thumb', 'social', 'email', 'print'] }, description: 'Restrict to assets suitable for these contexts. Use ["hero"] for email headers / banners.' },
      minQuality: { type: 'integer', minimum: 1, maximum: 5, description: 'Minimum quality_score (1-5). 4+ for hero, 3+ for inline body.' },
      brand: { type: 'string', enum: ['crab-place', 'crab-and-cruise'], description: 'Restrict to one brand. Default: both.' },
      limit: { type: 'integer', minimum: 1, maximum: 20, description: 'Max results. Default 8.' },
    },
  },
};

async function runMarketingStudioTool(name, input) {
  if (name !== 'search_media_library') throw new Error(`Unknown tool: ${name}`);
  const {
    query = '', category, subcategory, tags, usableFor, minQuality, brand, limit = 8,
  } = input || {};
  const rows = await neonQuery('media_assets_list', {
    search: query || '',
    brand: brand || null,
    category: category || null,
    subcategory: subcategory || null,
    minQuality: minQuality || 0,
    usableForAny: Array.isArray(usableFor) ? usableFor : [],
    tagsAll: Array.isArray(tags) ? tags : [],
    excludeTags: [],  // HEIC uploads are stored as JPEG - 'source-heic' is provenance, not a format problem
    starredOnly: false,
    includeArchived: false,
    limit: Math.min(Math.max(1, limit), 20),
    offset: 0,
  });
  // Compact shape — drop internal-only fields so Claude doesn't reason on them.
  return (rows || []).map((r) => ({
    id: r.id,
    url: r.r2_original_key ? `${SITE_URL}/.netlify/functions/r2-image?key=${encodeURIComponent(r.r2_original_key)}` : null,
    thumb_url: r.r2_thumb_key ? `${SITE_URL}/.netlify/functions/r2-image?key=${encodeURIComponent(r.r2_thumb_key)}` : null,
    category: r.category,
    subcategory: r.subcategory,
    suggested_name: r.suggested_name,
    alt_text: r.alt_text,
    tags: r.tags || [],
    quality_score: r.quality_score,
    width: r.width || null,
    height: r.height || null,
  }));
}

const SUGGESTED_PROMPTS = [
  "Build a Father's Day email for crab cakes",
  "Create a summer crab feast landing page",
  "Write a VIP club welcome email",
  "Build a holiday gift box email campaign",
  "Create a landing page for our new lobster tails",
  "Write a flash sale email — 20% off all crab meat",
];

// ─── API Call ───────────────────────────────────────────────
async function sendMessage(conversationHistory, brandAssets, strategies, stylePrefs, screenshotBase64, currentHtmlContext, userImages) {

  const systemPrompt = buildSystemPrompt(brandAssets, strategies, stylePrefs);

  // Strip non-API fields (like attachedImages) from conversation history
  let messages = conversationHistory.map(({ role, content }) => ({ role, content }));
  const hasContext = screenshotBase64 || currentHtmlContext || (userImages && userImages.length > 0);
  if (hasContext && messages.length > 0) {
    messages = messages.map((msg, i) => {
      if (i === messages.length - 1 && msg.role === 'user') {
        const content = [];
        // User-attached screenshots first
        if (userImages && userImages.length > 0) {
          for (const img of userImages) {
            content.push({ type: 'image', source: { type: 'base64', media_type: img.type || 'image/png', data: img.data } });
          }
        }
        // Auto-captured preview screenshot
        if (screenshotBase64) {
          content.push({ type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: screenshotBase64 } });
        }
        let textSuffix = '';
        if (userImages && userImages.length > 0) textSuffix += `\n\n(${userImages.length} screenshot${userImages.length > 1 ? 's' : ''} attached by the user — refer to ${userImages.length > 1 ? 'them' : 'it'} for visual context about what they want.)`;
        if (screenshotBase64) textSuffix += '\n\n(The auto-captured image shows the current state of the preview.)';
        if (currentHtmlContext) textSuffix += '\n\nHere is the CURRENT HTML of the preview for reference so you can see the current state. DO NOT return raw HTML — you MUST return your changes as a JSON object with the "landingPage" or "email" structure as described in your system prompt. Update the relevant fields (headline, subheadline, sections, ctaText, etc.) to reflect the requested changes:\n```html\n' + currentHtmlContext + '\n```';
        content.push({ type: 'text', text: msg.content + textSuffix });
        return { role: 'user', content };
      }
      return msg;
    });
  }

  // Tool-use loop. Claude can call search_media_library; we run it locally and
  // feed the result back until Claude returns a final text response (or we
  // hit the iteration cap).
  let currentMessages = messages;
  for (let iter = 0; iter < TOOL_LOOP_MAX; iter++) {
    const data = await callClaude({
      model: MODEL,
      max_tokens: 4000,
      system: systemPrompt,
      tools: [MEDIA_LIBRARY_SEARCH_TOOL],
      messages: currentMessages,
    });
    const blocks = data.content || [];
    const toolUses = blocks.filter((b) => b.type === 'tool_use');

    if (toolUses.length === 0) {
      // Final turn — extract text and parse JSON.
      const textBlock = blocks.find((b) => b.type === 'text');
      const rawText = textBlock?.text || '';
      try {
        const cleaned = rawText.replace(/```json|```/g, '').trim();
        return JSON.parse(cleaned);
      } catch {
        return { type: 'conversation', conversation: rawText };
      }
    }

    // Append the assistant's tool-use turn verbatim (required by the API).
    currentMessages = [...currentMessages, { role: 'assistant', content: blocks }];

    // Execute each tool call and collect results. One tool_result per tool_use_id.
    const toolResults = [];
    for (const block of toolUses) {
      try {
        const result = await runMarketingStudioTool(block.name, block.input || {});
        let payload = JSON.stringify(result);
        // 20KB cap — prevents runaway token use if the library has thousands
        // of rows that matched a broad query. The model should refine instead.
        if (payload.length > 20000) payload = payload.slice(0, 20000) + '\n... (truncated; narrow your search)';
        toolResults.push({ type: 'tool_result', tool_use_id: block.id, content: payload });
      } catch (e) {
        toolResults.push({ type: 'tool_result', tool_use_id: block.id, content: `Error: ${e.message}`, is_error: true });
      }
    }
    currentMessages = [...currentMessages, { role: 'user', content: toolResults }];
  }

  throw new Error(`Marketing Studio tool-use loop exceeded ${TOOL_LOOP_MAX} iterations without a final response`);
}

// ─── Email HTML Export ──────────────────────────────────────
function generateEmailHtml(email) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
  <title>${email.subject}</title>
</head>
<body style="margin:0;padding:0;font-family:'Montserrat',Arial,sans-serif;background-color:#f0f2f5">
  ${email.previewText ? `<div style="display:none;max-height:0;overflow:hidden">${email.previewText}</div>` : ''}
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f0f2f5">
    <tr><td align="center" style="padding:20px 0">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden">
        <tr><td style="background-color:#00395C;padding:20px 30px;text-align:center">
          <span style="color:#ffffff;font-family:Montserrat,sans-serif;font-size:20px;font-weight:700">🦀 CrabPlace.com</span>
        </td></tr>
        <tr><td style="background:linear-gradient(135deg,#00395C 0%,#002640 100%);padding:40px 30px;text-align:center">
          <h1 style="color:#ffffff;font-family:Montserrat,sans-serif;font-size:28px;font-weight:800;margin:0;line-height:1.3">${email.headline}</h1>
        </td></tr>
        <tr><td style="padding:30px;font-family:Montserrat,sans-serif;font-size:15px;line-height:1.6;color:#333333">
          ${email.body}
        </td></tr>
        ${email.ctaText ? `<tr><td style="padding:0 30px 30px;text-align:center">
          <a href="${email.ctaUrl || '#'}" style="background-color:#DC582A;color:#ffffff;padding:14px 32px;border-radius:6px;text-decoration:none;font-family:Montserrat,sans-serif;font-weight:700;display:inline-block;font-size:16px">${email.ctaText}</a>
        </td></tr>` : ''}
        <tr><td style="background-color:#00395C;padding:20px 30px;text-align:center">
          <p style="color:#FCD299;font-family:Montserrat,sans-serif;font-size:12px;margin:0">CrabPlace.com — Est. 1995</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

// ─── Landing Page HTML Export ───────────────────────────────
function generateLandingPageHtml(lp) {
  // Strip any nav/header elements Claude may have included in sections (we provide our own)
  const stripNav = (html) => html.replace(/<nav[^>]*>[\s\S]*?<\/nav>/gi, '').replace(/<header[^>]*>[\s\S]*?<\/header>/gi, '');
  const sectionsHtml = (lp.sections || []).map((s, i) =>
    `<div class="lp-section" style="max-width:800px;margin:0 auto;text-align:center">${stripNav(s.content)}</div>`
  ).join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
  <title>${lp.headline}</title>
  <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Montserrat',sans-serif;color:#333}.lp-section img{max-width:100%!important;height:auto!important;display:block!important;margin-left:auto!important;margin-right:auto!important;float:none!important}.lp-section div,.lp-section p,.lp-section span,.lp-section h1,.lp-section h2,.lp-section h3,.lp-section h4{text-align:center!important;float:none!important}.lp-section>div{margin-left:auto!important;margin-right:auto!important}</style>
</head>
<body>
  <nav style="background:#00395C;padding:16px 30px;display:flex;align-items:center;justify-content:space-between">
    <span style="color:#fff;font-weight:700;font-size:18px">🦀 CrabPlace.com</span>
    <div style="display:flex;gap:24px">
      <a href="https://crabplace.com" style="color:#FCD299;text-decoration:none;font-size:14px">Shop</a>
      <a href="https://crabplace.com/account.php?action=account_details" style="color:#FCD299;text-decoration:none;font-size:14px">My Account</a>
      <a href="https://crabplace.com/about-us/" style="color:#FCD299;text-decoration:none;font-size:14px">About</a>
    </div>
  </nav>
  ${(lp.sections && lp.sections.length > 0) ? '' : `<div style="background:linear-gradient(135deg,#00395C,#002640);padding:60px 30px;text-align:center">
    <h1 style="color:#fff;font-size:36px;font-weight:800;line-height:1.3;margin-bottom:16px">${lp.headline}</h1>
    ${lp.subheadline ? `<p style="color:#FCD299;font-size:18px;margin-bottom:24px">${lp.subheadline}</p>` : ''}
    ${lp.ctaText ? `<a href="${lp.ctaUrl || '#'}" style="background:#DC582A;color:#fff;padding:16px 36px;border-radius:6px;text-decoration:none;font-weight:700;display:inline-block;font-size:16px">${lp.ctaText}</a>` : ''}
  </div>`}
  ${sectionsHtml}
  <div style="background:#00395C;padding:30px;text-align:center">
    <p style="color:#fff;font-size:18px;font-weight:700;margin-bottom:8px">🦀 CrabPlace.com</p>
    <p style="color:#FCD299;font-size:13px">The First Online Seafood Retailer — Est. 1995</p>
  </div>
</body>
</html>`;
}

// ─── Main Component ─────────────────────────────────────────
export default function MarketingStudio() {
  const [messages, setMessages] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('start');
  const [currentEmail, setCurrentEmail] = useState(null);
  const [currentLandingPage, setCurrentLandingPage] = useState(null);
  const [input, setInput] = useState('');
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedLp, setCopiedLp] = useState(false);
  const [ddPushStatus, setDdPushStatus] = useState(null); // null | 'pushing' | 'success' | 'error'
  const [ddPushMsg, setDdPushMsg] = useState('');
  const [publishSlug, setPublishSlug] = useState('');
  const [publishStatus, setPublishStatus] = useState(null); // null | 'publishing' | 'success' | 'error'
  const [publishMsg, setPublishMsg] = useState('');
  const [showPublish, setShowPublish] = useState(false);
  const [isListening, setIsListening] = useState(false);
  // Brand Kit state
  const [brandAssets, setBrandAssets] = useState([]);
  const [brandLoading, setBrandLoading] = useState(true);
  const [addingAsset, setAddingAsset] = useState(null); // null | 'image' | 'block'
  const [assetForm, setAssetForm] = useState({ name: '', category: '', url: '', html: '' });
  const [editingAsset, setEditingAsset] = useState(null);
  const [bulkPaste, setBulkPaste] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [folderPath, setFolderPath] = useState([]); // [] = root, ['Products'] = one level deep, ['Products', 'Hard Crabs'] = two levels
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  // Strategies state
  const [strategies, setStrategies] = useState([]);
  const [strategiesLoading, setStrategiesLoading] = useState(true);
  const [addingStrategy, setAddingStrategy] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [strategyForm, setStrategyForm] = useState({ category: 'General', title: '', detail: '' });
  // Conversations state
  const [sessionId, setSessionId] = useState(() => 'session_' + Date.now());
  const [savedConversations, setSavedConversations] = useState([]);
  const [convoListOpen, setConvoListOpen] = useState(false);
  // Style preferences state
  const [stylePrefs, setStylePrefs] = useState([]);
  const [newPref, setNewPref] = useState('');
  // Image generation state
  const [showImageGen, setShowImageGen] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageSize, setImageSize] = useState('1024x1024');
  const [imageFolder, setImageFolder] = useState('');
  const [imageGenerating, setImageGenerating] = useState(false);
  const [imageHistory, setImageHistory] = useState([]); // [{prompt, url, key}, ...]
  const [editSourceAsset, setEditSourceAsset] = useState(null); // existing brand asset to edit
  const [previewImage, setPreviewImage] = useState(null); // {url, name} for lightbox
  const [userImages, setUserImages] = useState([]); // [{data: base64, type: 'image/png', preview: dataUrl}, ...]
  const [pickedMediaAssets, setPickedMediaAssets] = useState([]); // full rows from media_assets, attached to the next send
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const imageConvoRef = useRef(null);
  const messagesEndRef = useRef(null);
  const emailPreviewRef = useRef(null);
  const lpPreviewRef = useRef(null);
  const recognitionRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load all data on mount + scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
    neonQuery('brand_assets_list').then(rows => setBrandAssets(rows)).catch(e => console.error('Brand kit load error:', e)).finally(() => setBrandLoading(false));
    neonQuery('mkt_strategies_list').then(rows => setStrategies(rows)).catch(e => console.error('Strategies load error:', e)).finally(() => setStrategiesLoading(false));
    neonQuery('mkt_style_prefs_list').then(rows => setStylePrefs(rows)).catch(e => console.error('Style prefs load error:', e));
    neonQuery('mkt_conversations_list').then(rows => setSavedConversations(rows)).catch(e => console.error('Conversations load error:', e));
  }, []);

  // Auto-save conversation after each message exchange
  const saveConversation = useCallback(async (msgs, history) => {
    if (history.length === 0) return;
    const firstUserMsg = msgs.find(m => m.role === 'user');
    const title = firstUserMsg ? firstUserMsg.content.slice(0, 80) : 'Untitled';
    try {
      await neonQuery('mkt_conversations_save', { session_id: sessionId, title, messages: JSON.stringify(msgs) });
      const rows = await neonQuery('mkt_conversations_list');
      setSavedConversations(rows);
    } catch (e) { console.error('Save conversation error:', e); }
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-scroll image conversation
  useEffect(() => {
    if (imageConvoRef.current) {
      imageConvoRef.current.scrollTop = imageConvoRef.current.scrollHeight;
    }
  }, [imageHistory, imageGenerating]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [input]);

  // Speech recognition
  const toggleMic = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Speech recognition not supported in this browser.'); return; }

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    let finalTranscript = input;
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += (finalTranscript ? ' ' : '') + event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setInput(finalTranscript + (interim ? ' ' + interim : ''));
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognition.start();
    setIsListening(true);
  }, [isListening, input]);

  const handleSend = useCallback(async (text) => {
    const attachedImages = [...userImages];
    const userMessage = { role: 'user', content: text, attachedImages: attachedImages.length > 0 ? attachedImages : undefined };
    const newHistory = [...conversationHistory, userMessage];
    setMessages(prev => [...prev, userMessage]);
    setConversationHistory(newHistory);
    setIsLoading(true);
    setInput('');
    setUserImages([]);

    // Capture current preview context — send the actual HTML so Claude can see the layout
    let screenshotBase64 = null;
    let currentHtmlContext = null;
    try {
      if (activeTab === 'email' && currentEmail?.email) {
        currentHtmlContext = generateEmailHtml(currentEmail.email);
      } else if (activeTab === 'landing' && currentLandingPage?.landingPage) {
        currentHtmlContext = generateLandingPageHtml(currentLandingPage.landingPage);
      }
      // Also try html2canvas screenshot
      const previewEl = activeTab === 'email' ? emailPreviewRef.current
        : activeTab === 'landing' ? lpPreviewRef.current : null;
      if (previewEl) {
        const canvas = await html2canvas(previewEl, {
          scale: 0.75, useCORS: true, allowTaint: true, logging: false,
          backgroundColor: '#ffffff',
        });
        screenshotBase64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
        console.log('Screenshot captured:', Math.round(screenshotBase64.length / 1024) + 'KB');
      }
    } catch (e) { console.warn('Screenshot capture failed:', e); }

    // Show indicator in chat
    if (screenshotBase64 || currentHtmlContext) {
      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.role === 'user') {
          updated[updated.length - 1] = { ...last, hasScreenshot: !!screenshotBase64, hasHtmlContext: !!currentHtmlContext };
        }
        return updated;
      });
    }

    try {
      const parsed = await sendMessage(newHistory, brandAssets, strategies, stylePrefs, screenshotBase64, currentHtmlContext, attachedImages);
      let displayText = parsed.conversation || parsed.thinking || '';

      if ((parsed.type === 'email' || parsed.type === 'both') && parsed.email) {
        displayText = displayText || `Here's your email: "${parsed.email.subject}"`;
        setCurrentEmail(parsed);
        setActiveTab('email');
      }
      if ((parsed.type === 'landing_page' || parsed.type === 'both') && parsed.landingPage) {
        displayText = displayText || `Here's your landing page: "${parsed.landingPage.headline}"`;
        setCurrentLandingPage(parsed);
        if (!parsed.email) setActiveTab('landing');
      }
      if (!displayText) displayText = 'Content generated — check the preview tabs.';

      const raw = JSON.stringify(parsed);
      const newMsgs = [...messages, userMessage, { role: 'assistant', content: raw, displayText, parsed }];
      setMessages(prev => [...prev, { role: 'assistant', content: raw, displayText, parsed }]);
      setConversationHistory(prev => [...prev, { role: 'assistant', content: raw }]);
      // Auto-save conversation
      saveConversation(newMsgs, [...newHistory, { role: 'assistant', content: raw }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', displayText: `Error: ${err.message}`, parsed: null }]);
    } finally {
      setIsLoading(false);
    }
  }, [conversationHistory, brandAssets, strategies, stylePrefs, messages, saveConversation, activeTab, userImages]);

  // ── Brand Kit actions ──
  const addBrandAsset = async () => {
    const { name, category, url, html } = assetForm;
    if (!name.trim()) return;
    try {
      const rows = await neonQuery('brand_assets_add', { asset_type: addingAsset, name: name.trim(), category: category.trim(), url: url.trim(), html });
      setBrandAssets(rows);
    } catch (e) { console.error('Add asset error:', e); }
    setAddingAsset(null);
    setAssetForm({ name: '', category: '', url: '', html: '' });
  };

  const updateBrandAsset = async () => {
    if (!editingAsset) return;
    try {
      const rows = await neonQuery('brand_assets_update', { id: editingAsset.id, name: assetForm.name.trim(), category: assetForm.category.trim(), url: assetForm.url.trim(), html: assetForm.html });
      setBrandAssets(rows);
    } catch (e) { console.error('Update asset error:', e); }
    setEditingAsset(null);
    setAssetForm({ name: '', category: '', url: '', html: '' });
  };

  const removeBrandAsset = async (id) => {
    try {
      const rows = await neonQuery('brand_assets_remove', { id });
      setBrandAssets(rows);
    } catch (e) { console.error('Remove asset error:', e); }
  };

  const [dragOver, setDragOver] = useState(false);
  const [imageStudioDragOver, setImageStudioDragOver] = useState(false);
  const [selectedOverlays, setSelectedOverlays] = useState([]); // queued overlay types
  const [uploading, setUploading] = useState(0); // count of files currently uploading

  const resizeImage = (file, maxWidth = 1200) => new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const base64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
      resolve(base64);
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });

  const uploadFilesToBrandKit = async (files) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    const category = folderPath.length > 0 ? folderPath.join('/') : 'Uncategorized';
    setUploading(imageFiles.length);
    let latestRows = brandAssets;
    for (const file of imageFiles) {
      try {
        const base64 = await resizeImage(file);
        const resp = await fetch('/.netlify/functions/r2-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: file.name, contentType: 'image/jpeg', data: base64 }),
        });
        const { url } = await resp.json();
        const name = file.name.replace(/\.[^.]+$/, '');
        latestRows = await neonQuery('brand_assets_add', { asset_type: 'image', name, category, url });
      } catch (e) { console.error('Upload error:', e); }
      setUploading(prev => prev - 1);
    }
    setBrandAssets(latestRows);
    setUploading(0);
  };

  const handleBrandKitDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!e.dataTransfer.files?.length) return;
    // When Image Studio is open, redirect image drops to it for editing
    if (showImageGen) {
      const file = Array.from(e.dataTransfer.files).find(f => f.type.startsWith('image/'));
      if (file) { uploadFileForEdit(file); return; }
    }
    uploadFilesToBrandKit(e.dataTransfer.files);
  };

  const startEdit = (asset) => {
    setEditingAsset(asset);
    setAddingAsset(null);
    setAssetForm({ name: asset.name, category: asset.category || '', url: asset.url || '', html: asset.html || '' });
  };

  const bulkImportImages = async () => {
    const lines = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    setBulkImporting(true);
    let latestRows = brandAssets;
    for (const line of lines) {
      // Support two formats:
      // 1. Just a URL — auto-name from filename
      // 2. "Name | URL" — explicit name
      let name, url;
      if (line.includes('|')) {
        const parts = line.split('|').map(p => p.trim());
        name = parts[0];
        url = parts[1];
      } else {
        url = line;
        // Extract filename from URL as name
        try {
          const pathname = new URL(url).pathname;
          name = decodeURIComponent(pathname.split('/').pop().replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
        } catch {
          name = url.slice(-40);
        }
      }
      if (!url.startsWith('http')) continue;
      try {
        latestRows = await neonQuery('brand_assets_add', { asset_type: 'image', name, category: bulkCategory.trim() || null, url });
      } catch (e) { console.error('Bulk add error:', e); }
    }
    setBrandAssets(latestRows);
    setBulkImporting(false);
    setBulkText('');
  };

  // ── Strategy actions ──
  const addStrategy = async () => {
    const { category, title, detail } = strategyForm;
    if (!title.trim()) return;
    try {
      const rows = await neonQuery('mkt_strategies_add', { category: category.trim() || 'General', title: title.trim(), detail: detail.trim() });
      setStrategies(rows);
    } catch (e) { console.error('Add strategy error:', e); }
    setAddingStrategy(false);
    setStrategyForm({ category: 'General', title: '', detail: '' });
  };

  const updateStrategy = async () => {
    if (!editingStrategy) return;
    try {
      const rows = await neonQuery('mkt_strategies_update', { id: editingStrategy.id, category: strategyForm.category.trim(), title: strategyForm.title.trim(), detail: strategyForm.detail.trim() });
      setStrategies(rows);
    } catch (e) { console.error('Update strategy error:', e); }
    setEditingStrategy(null);
    setStrategyForm({ category: 'General', title: '', detail: '' });
  };

  const removeStrategy = async (id) => {
    try {
      const rows = await neonQuery('mkt_strategies_remove', { id });
      setStrategies(rows);
    } catch (e) { console.error('Remove strategy error:', e); }
  };

  // ── Conversation actions ──
  const loadConversation = async (conv) => {
    try {
      const rows = await neonQuery('mkt_conversations_load', { session_id: conv.session_id });
      if (rows.length > 0) {
        const loaded = rows[0];
        const msgs = typeof loaded.messages === 'string' ? JSON.parse(loaded.messages) : loaded.messages;
        setMessages(msgs);
        // Rebuild conversationHistory from messages (only role + content for API)
        setConversationHistory(msgs.filter(m => m.content).map(m => ({ role: m.role, content: m.content })));
        setSessionId(conv.session_id);
        // Restore email/landing page from last assistant message
        const lastAssistant = [...msgs].reverse().find(m => m.parsed);
        if (lastAssistant?.parsed?.email) setCurrentEmail(lastAssistant.parsed);
        if (lastAssistant?.parsed?.landingPage) setCurrentLandingPage(lastAssistant.parsed);
        setActiveTab('start');
        setConvoListOpen(false);
      }
    } catch (e) { console.error('Load conversation error:', e); }
  };

  const startNewConversation = () => {
    setMessages([]);
    setConversationHistory([]);
    setSessionId('session_' + Date.now());
    setCurrentEmail(null);
    setCurrentLandingPage(null);
    setActiveTab('start');
    setConvoListOpen(false);
  };

  const deleteConversation = async (session_id) => {
    try {
      const rows = await neonQuery('mkt_conversations_delete', { session_id });
      setSavedConversations(rows);
    } catch (e) { console.error('Delete conversation error:', e); }
  };

  // ── Style preference actions ──
  const addStylePref = async (pref, source) => {
    if (!pref.trim()) return;
    try {
      const rows = await neonQuery('mkt_style_prefs_add', { pref: pref.trim(), source: source || null });
      setStylePrefs(rows);
    } catch (e) { console.error('Add style pref error:', e); }
  };

  const removeStylePref = async (id) => {
    try {
      const rows = await neonQuery('mkt_style_prefs_remove', { id });
      setStylePrefs(rows);
    } catch (e) { console.error('Remove style pref error:', e); }
  };

  // ── Image generation ──
  const generateImage = async () => {
    if (!imagePrompt.trim()) return;
    setImageGenerating(true);
    try {
      const lastImage = imageHistory.length > 0 ? imageHistory[imageHistory.length - 1] : null;
      const sourceKey = lastImage?.key || editSourceAsset?.r2Key || null;
      const sourceUrl = !sourceKey && editSourceAsset?.url ? editSourceAsset.url : null;
      const isEdit = !!(sourceKey || sourceUrl);

      const res = await fetch('/.netlify/functions/generate-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: imagePrompt.trim(),
          size: isEdit ? 'auto' : imageSize,
          filename: imagePrompt.trim().slice(0, 40),
          sourceKey,
          sourceUrl,
        }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(res.ok ? 'Invalid response from server' : `Server error (${res.status}): ${text.slice(0, 100)}`); }
      if (!res.ok) throw new Error(data.error || data.detail?.error?.message || 'Generation failed');

      if (data.text && !data.url) {
        // GPT responded with text only (no image generated) — show as assistant message
        setImageHistory(prev => [...prev, { prompt: imagePrompt.trim(), assistantText: data.text }]);
      } else {
        setImageHistory(prev => [...prev, { prompt: imagePrompt.trim(), url: data.url, key: data.key }]);
      }
      setImagePrompt('');
    } catch (err) {
      setImageHistory(prev => [...prev, { prompt: imagePrompt.trim(), error: err.message }]);
    } finally {
      setImageGenerating(false);
    }
  };

  const saveImageToBrandKit = async (historyItem) => {
    const category = imageFolder || (folderPath.length > 0 ? folderPath.join('/') : 'Generated');
    try {
      const rows = await neonQuery('brand_assets_add', { asset_type: 'image', name: historyItem.prompt.slice(0, 80), category, url: historyItem.url });
      setBrandAssets(rows);
    } catch (e) { console.error('Save to brand kit error:', e); }
  };

  const startEditExistingImage = (asset) => {
    setShowImageGen(true);
    setBulkPaste(false);
    setAddingAsset(null);
    setEditingAsset(null);
    setEditSourceAsset({ url: asset.url, name: asset.name });
    setImageHistory([{ prompt: `Editing: ${asset.name}`, url: asset.url, key: null, isSourcePreview: true }]);
    setImagePrompt('');
    setSelectedOverlays([]);
    setImageFolder(asset.category || (folderPath.length > 0 ? folderPath.join('/') : ''));
  };

  // Resize image for OpenAI edit — max 512px, JPEG to keep payload small and fast
  const resizeForEdit = (dataUrl, maxDim = 512) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const c = document.createElement('canvas');
      c.width = width;
      c.height = height;
      c.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(c.toDataURL('image/jpeg', 0.85));
    };
    img.src = dataUrl;
  });

  const uploadFileForEdit = (file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const rawDataUrl = reader.result;
      try {
        setImageGenerating(true);
        // Resize to max 1024px before uploading (keeps OpenAI requests fast)
        const dataUrl = await resizeForEdit(rawDataUrl);
        const base64 = dataUrl.split(',')[1];
        const isJpeg = dataUrl.startsWith('data:image/jpeg');
        const res = await fetch('/.netlify/functions/r2-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: 'pasted-image', contentType: isJpeg ? 'image/jpeg' : 'image/png', data: base64 }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Upload failed');
        setEditSourceAsset({ url: data.url, name: 'Pasted image', pasted: true });
        setSelectedOverlays([]);
        setImageHistory([{ prompt: 'Editing: Pasted image', url: dataUrl, key: data.key, isSourcePreview: true }]);
      } catch (err) {
        console.error('Image upload for edit error:', err);
      } finally {
        setImageGenerating(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // Canvas-based image overlay — applies one or more overlays to the same canvas
  const OVERLAY_LABELS = {
    'boxes-happy': '"Our Boxes Make People Happy"',
    'free-shipping': '"Free Shipping" banner',
    'sale-percent': 'Sale badge',
    'logo': 'CrabPlace logo',
  };

  const canvasOverlay = async (overlayTypes) => {
    const types = Array.isArray(overlayTypes) ? overlayTypes : [overlayTypes];
    if (!types.length) return;

    const lastImage = imageHistory.length > 0 ? imageHistory[imageHistory.length - 1] : null;
    const srcUrl = lastImage?.url || editSourceAsset?.url;
    if (!srcUrl) return;

    setImageGenerating(true);
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error('Failed to load image'));
        i.src = srcUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const W = canvas.width;
      const H = canvas.height;
      const pad = Math.round(W * 0.03);

      const fitText = (text, maxFontSize, maxWidth, weight = '700') => {
        let fs = maxFontSize;
        while (fs > 8) {
          ctx.font = `${weight} ${fs}px Montserrat, Arial, sans-serif`;
          if (ctx.measureText(text).width <= maxWidth) break;
          fs -= 1;
        }
        return fs;
      };

      // Apply overlays — logo always last so it draws on top of everything
      const sorted = [...types].sort((a, b) => (a === 'logo' ? 1 : 0) - (b === 'logo' ? 1 : 0));
      const hasBottomBanner = types.includes('boxes-happy') || types.includes('custom-text');
      const hasTopBanner = types.includes('free-shipping');

      for (const overlayType of sorted) {
        if (overlayType === 'boxes-happy') {
          const bannerH = Math.round(H * 0.12);
          const bannerY = H - bannerH;
          ctx.fillStyle = '#00395C';
          ctx.fillRect(0, bannerY, W, bannerH);
          ctx.fillStyle = '#DC582A';
          ctx.fillRect(0, bannerY, W, Math.max(3, Math.round(bannerH * 0.04)));
          const fs = fitText('Our Boxes Make People Happy', Math.round(bannerH * 0.5), W - pad * 2);
          ctx.font = `700 ${fs}px Montserrat, Arial, sans-serif`;
          ctx.fillStyle = '#FFFFFF';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('Our Boxes Make People Happy', W / 2, bannerY + bannerH / 2 + 1);
        } else if (overlayType === 'free-shipping') {
          const bannerH = Math.round(H * 0.08);
          ctx.fillStyle = '#DC582A';
          ctx.fillRect(0, 0, W, bannerH);
          const fs = fitText('FREE SHIPPING', Math.round(bannerH * 0.6), W - pad * 2, '800');
          ctx.font = `800 ${fs}px Montserrat, Arial, sans-serif`;
          ctx.fillStyle = '#FFFFFF';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('FREE SHIPPING', W / 2, bannerH / 2);
        } else if (overlayType === 'sale-percent') {
          const badgeSize = Math.round(Math.min(W, H) * 0.22);
          ctx.fillStyle = '#dc3545';
          ctx.beginPath();
          ctx.moveTo(W - badgeSize, 0);
          ctx.lineTo(W, 0);
          ctx.lineTo(W, badgeSize);
          ctx.closePath();
          ctx.fill();
          const fontSize = Math.round(badgeSize * 0.25);
          ctx.font = `800 ${fontSize}px Montserrat, Arial, sans-serif`;
          ctx.fillStyle = '#FFFFFF';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.save();
          ctx.translate(W - badgeSize * 0.3, badgeSize * 0.3);
          ctx.rotate(-Math.PI / 4);
          ctx.fillText('SALE', 0, 0);
          ctx.restore();
        } else if (overlayType === 'logo') {
          // Draw CrabPlace.com watermark using the PNG crab icon + text
          const crabImg = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = () => reject(new Error('Crab icon load failed'));
            i.src = '/logo192.png';
          });
          const logoFontSize = Math.round(H * 0.035);
          const logoText = 'CrabPlace.com';
          // Use Arial as reliable canvas font
          ctx.font = `bold ${logoFontSize}px Arial, sans-serif`;
          const textW = ctx.measureText(logoText).width;
          const iconSize = Math.round(logoFontSize * 1.4);
          const gap = Math.round(logoFontSize * 0.3);
          const logoPad = Math.round(W * 0.02);
          const pillW = iconSize + gap + textW + logoPad * 2;
          const pillH = Math.round(logoFontSize * 2);
          let lx = W - pillW - pad;
          let ly = H - pillH - pad;
          if (hasBottomBanner) ly -= Math.round(H * 0.13);
          // Semi-transparent navy pill background
          ctx.globalAlpha = 0.8;
          ctx.fillStyle = '#00395C';
          const r = pillH / 2;
          ctx.beginPath();
          ctx.moveTo(lx + r, ly);
          ctx.lineTo(lx + pillW - r, ly);
          ctx.arcTo(lx + pillW, ly, lx + pillW, ly + r, r);
          ctx.arcTo(lx + pillW, ly + pillH, lx + pillW - r, ly + pillH, r);
          ctx.lineTo(lx + r, ly + pillH);
          ctx.arcTo(lx, ly + pillH, lx, ly + pillH - r, r);
          ctx.arcTo(lx, ly, lx + r, ly, r);
          ctx.closePath();
          ctx.fill();
          ctx.globalAlpha = 1.0;
          // Crab icon
          const iconY = ly + (pillH - iconSize) / 2;
          ctx.drawImage(crabImg, lx + logoPad, iconY, iconSize, iconSize);
          // White text
          ctx.fillStyle = '#FFFFFF';
          ctx.font = `bold ${logoFontSize}px Arial, sans-serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(logoText, lx + logoPad + iconSize + gap, ly + pillH / 2);
        } else if (overlayType === 'custom-text') {
          const text = imagePrompt.trim();
          if (!text) continue;
          const bannerH = Math.round(H * 0.10);
          const bannerY = H - bannerH;
          ctx.fillStyle = '#00395C';
          ctx.fillRect(0, bannerY, W, bannerH);
          ctx.fillStyle = '#DC582A';
          ctx.fillRect(0, bannerY, W, Math.max(3, Math.round(bannerH * 0.04)));
          const fs = fitText(text, Math.round(bannerH * 0.5), W - pad * 2);
          ctx.font = `700 ${fs}px Montserrat, Arial, sans-serif`;
          ctx.fillStyle = '#FFFFFF';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(text, W / 2, bannerY + bannerH / 2 + 1);
        }
      }

      // Export canvas to blob, upload to R2
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const base64 = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(blob);
      });
      const res = await fetch('/.netlify/functions/r2-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: `overlay-${types.join('+')}`, contentType: 'image/png', data: base64 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');

      const labels = types.map(t => OVERLAY_LABELS[t] || (t === 'custom-text' ? `"${imagePrompt.trim()}"` : t));
      setImageHistory(prev => [...prev, { prompt: `Added: ${labels.join(' + ')}`, url: data.url, key: data.key }]);
      setImagePrompt('');
      setSelectedOverlays([]);
    } catch (err) {
      setImageHistory(prev => [...prev, { prompt: `Overlay error`, error: err.message }]);
    } finally {
      setImageGenerating(false);
    }
  };

  const readFileAsBase64 = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = dataUrl.split(',')[1];
      resolve({ data: base64, type: file.type || 'image/png', preview: dataUrl });
    };
    reader.readAsDataURL(file);
  });

  const handleImageFiles = async (files) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    const newImages = await Promise.all(imageFiles.map(readFileAsBase64));
    setUserImages(prev => [...prev, ...newImages]);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItems = Array.from(items).filter(item => item.type.startsWith('image/'));
    if (imageItems.length > 0) {
      e.preventDefault();
      const files = imageItems.map(item => item.getAsFile()).filter(Boolean);
      handleImageFiles(files);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if ((!input.trim() && !userImages.length && !pickedMediaAssets.length) || isLoading) return;
    let text = input.trim() || (userImages.length ? 'Here are the screenshots I want you to look at.' : 'Use these Media Library images.');
    if (pickedMediaAssets.length) {
      const siteUrl = 'https://cpx-prototype.netlify.app';
      const list = pickedMediaAssets.map((a) => {
        const url = `${siteUrl}/.netlify/functions/r2-image?key=${encodeURIComponent(a.r2_original_key || a.r2_thumb_key)}`;
        return `- ${url}  (${a.suggested_name} — ${a.alt_text || ''})`;
      }).join('\n');
      text = `${text}\n\nUse these specific Media Library images in the generated HTML (embed as <img src> with the listed alt text; do not call search_media_library for these):\n${list}`;
    }
    handleSend(text);
    setPickedMediaAssets([]);
  };

  // ─── Inline edit helpers ───
  const editEmail = (field, value) => {
    setCurrentEmail(prev => ({ ...prev, email: { ...prev.email, [field]: value } }));
  };
  const editLp = (field, value) => {
    setCurrentLandingPage(prev => ({ ...prev, landingPage: { ...prev.landingPage, [field]: value } }));
  };
  const editLpSection = (index, html) => {
    setCurrentLandingPage(prev => {
      const sections = [...(prev.landingPage.sections || [])];
      sections[index] = { ...sections[index], content: html };
      return { ...prev, landingPage: { ...prev.landingPage, sections } };
    });
  };

  const copyHtml = async (html, setter) => {
    await navigator.clipboard.writeText(html);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const DD_PROXY = 'https://crabplace-dotdigital.netlify.app/.netlify/functions';

  const pushToDotdigital = async (type) => {
    setDdPushStatus('pushing');
    setDdPushMsg('');
    try {
      let html, name, subject;
      if (type === 'email' && currentEmail?.email) {
        html = generateEmailHtml(currentEmail.email);
        name = `CPx: ${currentEmail.email.subject || 'Email'} — ${new Date().toLocaleDateString()}`;
        subject = currentEmail.email.subject || '';
      } else if (type === 'landing' && currentLandingPage?.landingPage) {
        html = generateLandingPageHtml(currentLandingPage.landingPage);
        name = `CPx: ${currentLandingPage.landingPage.headline || 'Landing Page'} — ${new Date().toLocaleDateString()}`;
        subject = '';
      } else {
        throw new Error('No content to push');
      }

      const res = await fetch(`${DD_PROXY}/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, subject, htmlContent: html }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || data.message || 'Push failed');
      setDdPushStatus('success');
      setDdPushMsg(`Template "${name}" created (ID: ${data.id || 'unknown'})`);
      setTimeout(() => setDdPushStatus(null), 5000);
    } catch (e) {
      console.error('Dotdigital push error:', e);
      setDdPushStatus('error');
      setDdPushMsg(e.message);
      setTimeout(() => setDdPushStatus(null), 5000);
    }
  };

  const publishLandingPage = async () => {
    if (!publishSlug.trim() || !currentLandingPage?.landingPage) return;
    setPublishStatus('publishing');
    setPublishMsg('');
    try {
      const html = generateLandingPageHtml(currentLandingPage.landingPage);
      const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const res = await fetch('/.netlify/functions/publish-landing-page', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: publishSlug.trim(),
          title: currentLandingPage.landingPage.headline || publishSlug.trim(),
          html,
          publishedBy: user.staffName || 'unknown',
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Publish failed');
      setPublishStatus('success');
      setPublishMsg(data.url || data.fallbackUrl);
    } catch (e) {
      console.error('Publish error:', e);
      setPublishStatus('error');
      setPublishMsg(e.message);
    }
  };

  // ─── Render ─────────────────────────────────────────────
  return (
    <div style={s.wrapper}>
      {/* Image lightbox */}
      {previewImage && (
        <div onClick={() => setPreviewImage(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, cursor: 'pointer',
        }}>
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img src={previewImage.url} alt={previewImage.name || ''} style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }} />
            {previewImage.name && (
              <div style={{ textAlign: 'center', color: '#fff', fontSize: 13, marginTop: 10, fontWeight: 600 }}>{previewImage.name}</div>
            )}
            <button onClick={() => setPreviewImage(null)} style={{
              position: 'absolute', top: -12, right: -12, width: 28, height: 28, borderRadius: '50%',
              background: '#fff', border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 700, color: '#333',
              display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}>x</button>
          </div>
        </div>
      )}
      {/* ─── LEFT: Chat Panel ─── */}
      <div style={s.chat}>
        <div style={s.chatHeader}>
          <span style={{ fontSize: 22 }}>🦀</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>Marketing Studio</div>
            <div style={{ fontSize: 11, color: '#FCD299' }}>AI Content Generator</div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={startNewConversation} title="New conversation"
              style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 4, color: '#fff', cursor: 'pointer', padding: '4px 8px', fontSize: 11, fontWeight: 600, fontFamily: 'inherit' }}>
              + New
            </button>
            <button onClick={() => setConvoListOpen(!convoListOpen)} title="Past conversations"
              style={{ background: convoListOpen ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 4, color: '#fff', cursor: 'pointer', padding: '4px 8px', fontSize: 11, fontFamily: 'inherit' }}>
              History
            </button>
          </div>
        </div>

        {/* Conversation list dropdown */}
        {convoListOpen && (
          <div style={{ background: '#002640', borderBottom: '1px solid #00395C', maxHeight: 200, overflowY: 'auto' }}>
            {savedConversations.length === 0 ? (
              <div style={{ padding: '12px 16px', fontSize: 11, color: '#888' }}>No saved conversations yet.</div>
            ) : savedConversations.map(conv => (
              <div key={conv.session_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}
                onClick={() => loadConversation(conv)}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {conv.title || 'Untitled'}
                  </div>
                  <div style={{ fontSize: 9, color: '#888' }}>
                    {new Date(conv.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {conv.message_count} messages
                  </div>
                </div>
                <button onClick={e => { e.stopPropagation(); deleteConversation(conv.session_id); }} title="Delete"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
                  <Trash2 size={11} color="#dc3545" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={s.chatMessages}>
          {messages.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#00395C', marginBottom: 8 }}>What would you like to create?</div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 16, lineHeight: 1.5 }}>
                Describe your campaign and I'll generate branded email and landing page content.
              </div>
              {SUGGESTED_PROMPTS.map((p, i) => (
                <div key={i} onClick={() => !isLoading && handleSend(p)}
                  style={s.promptChip}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#DC582A'; e.currentTarget.style.background = '#FFF8F5'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}>
                  {p}
                </div>
              ))}
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} style={msg.role === 'user' ? s.userRow : s.assistRow}>
              {msg.role === 'assistant' && <span style={{ fontSize: 16, marginTop: 2 }}>🦀</span>}
              <div style={msg.role === 'user' ? s.userBubble : s.assistBubble}>
                {msg.attachedImages && msg.attachedImages.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginBottom: 6, flexWrap: 'wrap' }}>
                    {msg.attachedImages.map((img, j) => (
                      <img key={j} src={img.preview} alt="attached" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4, border: '1px solid rgba(255,255,255,0.3)' }} />
                    ))}
                  </div>
                )}
                {(msg.hasScreenshot || msg.hasHtmlContext) && <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', marginBottom: 3 }}>{msg.hasScreenshot ? '📸' : '📄'} preview context attached</div>}
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.displayText || msg.content}</div>
                {msg.parsed && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    {(msg.parsed.type === 'email' || msg.parsed.type === 'both') && msg.parsed.email && (
                      <button style={s.viewBtn} onClick={() => { setCurrentEmail(msg.parsed); setActiveTab('email'); }}>View Email</button>
                    )}
                    {(msg.parsed.type === 'landing_page' || msg.parsed.type === 'both') && msg.parsed.landingPage && (
                      <button style={s.viewBtn} onClick={() => { setCurrentLandingPage(msg.parsed); setActiveTab('landing'); }}>View Landing Page</button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div style={s.assistRow}>
              <span style={{ fontSize: 16, marginTop: 2 }}>🦀</span>
              <div style={s.assistBubble}>
                <span className="marketing-dots">Generating...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form style={s.inputBar} onSubmit={handleSubmit}>
          {userImages.length > 0 && (
            <div style={{ display: 'flex', gap: 6, padding: '6px 10px 0', flexWrap: 'wrap', alignItems: 'center' }}>
              {userImages.map((img, i) => (
                <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={img.preview} alt="attached" style={{ width: 56, height: 42, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} />
                  <button type="button" onClick={() => setUserImages(prev => prev.filter((_, j) => j !== i))}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#DC582A', color: '#fff', border: 'none', borderRadius: '50%', width: 16, height: 16, fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, padding: 0 }}>x</button>
                </div>
              ))}
            </div>
          )}
          {pickedMediaAssets.length > 0 && (
            <div style={{ display: 'flex', gap: 6, padding: '6px 10px 0', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>From Media Library:</span>
              {pickedMediaAssets.map((a) => (
                <div key={a.id} style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={`/.netlify/functions/r2-image?key=${encodeURIComponent(a.r2_thumb_key)}`}
                    alt={a.alt_text || ''} title={`${a.suggested_name} — ${a.alt_text || ''}`}
                    style={{ width: 56, height: 42, objectFit: 'cover', borderRadius: 4, border: '2px solid #DC582A' }} />
                  <button type="button"
                    onClick={() => setPickedMediaAssets(prev => prev.filter((x) => x.id !== a.id))}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#DC582A', color: '#fff', border: 'none', borderRadius: '50%', width: 16, height: 16, fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, padding: 0 }}>x</button>
                </div>
              ))}
            </div>
          )}
          <div style={s.inputWrap}>
            <input type="file" ref={fileInputRef} accept="image/*" multiple style={{ display: 'none' }}
              onChange={e => { handleImageFiles(e.target.files); e.target.value = ''; }} />
            <textarea ref={textareaRef} style={s.textarea} value={input}
              onChange={e => setInput(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
              placeholder="Describe your campaign... (Shift+Enter for new line, or paste a screenshot)"
              disabled={isLoading} rows={2} />
            <div style={s.inputActions}>
              <button type="button" style={{ ...s.micBtn, color: '#999', background: 'transparent' }}
                onClick={() => fileInputRef.current?.click()} title="Attach screenshot">
                📎
              </button>
              <button type="button" style={{ ...s.micBtn, color: pickedMediaAssets.length ? '#DC582A' : '#999', background: pickedMediaAssets.length ? '#FFF0EB' : 'transparent' }}
                onClick={() => setMediaPickerOpen(true)} title="Pick from Media Library">
                🖼️
              </button>
              <button type="button" style={{ ...s.micBtn, color: isListening ? '#DC582A' : '#999', background: isListening ? '#FFF0EB' : 'transparent' }}
                onClick={toggleMic} title={isListening ? 'Stop listening' : 'Voice input'}>
                {isListening ? '⏹' : '🎤'}
              </button>
              <button type="submit" style={{ ...s.sendBtn, opacity: (!input.trim() && !userImages.length && !pickedMediaAssets.length) || isLoading ? 0.5 : 1 }}
                disabled={(!input.trim() && !userImages.length && !pickedMediaAssets.length) || isLoading}>Send</button>
            </div>
          </div>
        </form>
        {mediaPickerOpen ? (
          <MediaPicker
            multiSelect
            initialSelection={pickedMediaAssets.map((a) => a.id)}
            onClose={() => setMediaPickerOpen(false)}
            onPick={(assets) => { setPickedMediaAssets(assets); setMediaPickerOpen(false); }}
          />
        ) : null}
      </div>

      {/* ─── RIGHT: Preview Panel ─── */}
      <div style={s.preview}>
        <div style={s.tabs}>
          {['start', 'email', 'landing', 'brand-kit', 'strategies'].map(tab => (
            <button key={tab}
              style={activeTab === tab ? s.tabActive : s.tab}
              disabled={(tab === 'email' && !currentEmail) || (tab === 'landing' && !currentLandingPage)}
              onClick={() => {
                if (tab === 'email' && !currentEmail) return;
                if (tab === 'landing' && !currentLandingPage) return;
                setActiveTab(tab);
              }}>
              {tab === 'start' ? 'Getting Started' : tab === 'email' ? 'Email Preview' : tab === 'landing' ? 'Landing Page' : tab === 'brand-kit' ? `Brand Kit${brandAssets.length ? ` (${brandAssets.length})` : ''}` : `Knowledge${strategies.length + stylePrefs.length ? ` (${strategies.length + stylePrefs.length})` : ''}`}
            </button>
          ))}
        </div>

        <div style={s.tabContent}>
          {/* Getting Started */}
          {activeTab === 'start' && (
            <div style={s.startPanel}>
              <div style={s.startCard}>
                <div style={{ textAlign: 'center', fontSize: 40, marginBottom: 12 }}>🦀</div>
                <h2 style={{ textAlign: 'center', fontSize: 22, fontWeight: 800, color: '#00395C', marginBottom: 8 }}>CrabPlace Marketing Studio</h2>
                <p style={{ textAlign: 'center', fontSize: 13, color: '#666', lineHeight: 1.6, marginBottom: 20 }}>
                  AI-powered marketing assistant for creating branded email campaigns and landing pages — in CrabPlace's voice.
                </p>

                {/* Quick Reference Guide */}
                <div style={{ textAlign: 'left', fontSize: 12, color: '#333', lineHeight: 1.7 }}>
                  <div style={{ background: '#00395C', color: '#fff', padding: '10px 14px', borderRadius: '8px 8px 0 0', fontWeight: 700, fontSize: 13 }}>Quick Reference Guide</div>
                  <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 16 }}>

                    <div style={{ fontWeight: 700, color: '#00395C', marginBottom: 4 }}>1. Create Content</div>
                    <div style={{ marginBottom: 12, paddingLeft: 12, fontSize: 11, color: '#555' }}>
                      Type what you need in the chat panel on the left. Be specific:<br />
                      <span style={{ color: '#DC582A' }}>"Build a Father's Day email for crab cakes with a 15% off code DAD15"</span><br />
                      <span style={{ color: '#DC582A' }}>"Create a winback landing page for inactive customers with a $25 gift card"</span><br />
                      Use the mic button to dictate instead of typing. Shift+Enter for new lines.
                    </div>

                    <div style={{ fontWeight: 700, color: '#00395C', marginBottom: 4 }}>2. Preview & Iterate</div>
                    <div style={{ marginBottom: 12, paddingLeft: 12, fontSize: 11, color: '#555' }}>
                      Generated content appears in the <strong>Email Preview</strong> and <strong>Landing Page</strong> tabs.<br />
                      Keep chatting to refine: "make the headline shorter", "add a section about gift boxes", "swap the CTA to Shop Now".
                    </div>

                    <div style={{ fontWeight: 700, color: '#00395C', marginBottom: 4 }}>3. Copy & Use</div>
                    <div style={{ marginBottom: 12, paddingLeft: 12, fontSize: 11, color: '#555' }}>
                      Click <strong>Copy HTML</strong> on any preview to get a complete, standalone HTML document.<br />
                      Paste directly into Dotdigital (HTML editor) or BigCommerce. Fonts, colors, and styles are all inline.
                    </div>

                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 4 }}>
                      <div style={{ fontWeight: 700, color: '#00395C', marginBottom: 6 }}>Tabs Overview</div>
                      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                        <tbody>
                          {[
                            ['Brand Kit', 'Your image library and HTML building blocks. Organized in folders. Add images via Bulk Paste (right-click in Dotdigital > Copy image address). Add footer/header HTML as Building Blocks. Everything here gets used by the AI automatically.'],
                            ['Knowledge', 'Strategies and style preferences that persist across conversations. Add marketing context ("scaling back volume for domain reputation") and style rules ("keep headlines under 8 words"). The AI reads all of this on every message.'],
                          ].map(([tab, desc], i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '6px 8px', fontWeight: 600, color: '#00395C', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{tab}</td>
                              <td style={{ padding: '6px 8px', color: '#555' }}>{desc}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 8 }}>
                      <div style={{ fontWeight: 700, color: '#00395C', marginBottom: 6 }}>Chat Panel Features</div>
                      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                        <tbody>
                          {[
                            ['+ New', 'Start a fresh conversation'],
                            ['History', 'Resume any past conversation — all auto-saved'],
                            ['Mic button', 'Dictate your brief instead of typing'],
                            ['View Email / View Landing Page', 'Buttons on AI responses to jump to that preview'],
                          ].map(([feat, desc], i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '4px 8px', fontWeight: 600, color: '#00395C', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{feat}</td>
                              <td style={{ padding: '4px 8px', color: '#555' }}>{desc}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 8 }}>
                      <div style={{ fontWeight: 700, color: '#00395C', marginBottom: 4 }}>Tips</div>
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#555' }}>
                        <li>The more detail you give, the better the output. Include promo codes, dates, audiences, and goals.</li>
                        <li>Ask for "both" to get an email and landing page in one shot.</li>
                        <li>Add your Dotdigital footer as a Building Block — the AI will include it in every email.</li>
                        <li>Use Knowledge strategies to capture decisions so you don't repeat yourself across sessions.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Email Preview */}
          {activeTab === 'email' && currentEmail?.email && (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={s.previewToolbar}>
                <div>
                  <div style={{ fontSize: 12 }}>Subject: <Editable tag="strong" value={currentEmail.email.subject} onChange={v => editEmail('subject', v)} /></div>
                  {currentEmail.email.previewText && <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>Preview: <Editable tag="span" value={currentEmail.email.previewText} onChange={v => editEmail('previewText', v)} /></div>}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button style={s.copyBtn} onClick={() => copyHtml(generateEmailHtml(currentEmail.email), setCopiedEmail)}>
                    {copiedEmail ? '✓ Copied!' : 'Copy HTML'}
                  </button>
                  <button style={{ ...s.copyBtn, background: ddPushStatus === 'pushing' ? '#999' : '#28a745' }}
                    onClick={() => pushToDotdigital('email')} disabled={ddPushStatus === 'pushing'}>
                    {ddPushStatus === 'pushing' ? 'Pushing...' : ddPushStatus === 'success' ? '✓ Pushed!' : 'Push to Dotdigital'}
                  </button>
                </div>
              </div>
              <div style={s.previewScroll}>
                <div ref={emailPreviewRef} style={s.emailFrame}>
                  <div style={{ background: '#00395C', padding: '14px 20px', textAlign: 'center', color: '#fff', fontWeight: 700, fontSize: 16 }}>🦀 CrabPlace.com</div>
                  <div style={{ background: 'linear-gradient(135deg, #00395C, #002640)', padding: '32px 20px', textAlign: 'center' }}>
                    <Editable tag="h2" value={currentEmail.email.headline} onChange={v => editEmail('headline', v)}
                      style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: 0, lineHeight: 1.3 }} />
                    {currentEmail.email.heroImage && <p style={{ color: '#FCD299', fontSize: 11, marginTop: 10, fontStyle: 'italic' }}>[{currentEmail.email.heroImage}]</p>}
                  </div>
                  <Editable tag="div" value={currentEmail.email.body} onChange={v => editEmail('body', v)} isHtml
                    style={{ padding: 20, fontSize: 13, lineHeight: 1.7, color: '#333' }} />
                  {currentEmail.email.ctaText && (
                    <div style={{ padding: '0 20px 20px', textAlign: 'center' }}>
                      <Editable tag="a" value={currentEmail.email.ctaText} onChange={v => editEmail('ctaText', v)}
                        style={{ display: 'inline-block', padding: '12px 28px', background: '#DC582A', color: '#fff', borderRadius: 6, textDecoration: 'none', fontWeight: 700, fontSize: 14 }} />
                    </div>
                  )}
                  <div style={{ background: '#00395C', padding: '14px 20px', textAlign: 'center' }}>
                    <p style={{ color: '#FCD299', fontSize: 10, margin: 0 }}>CrabPlace.com — Est. 1995</p>
                  </div>
                </div>
              </div>
              {currentEmail.thinking && <div style={s.thinking}><strong>Approach:</strong> {currentEmail.thinking}</div>}
            </div>
          )}

          {/* Landing Page Preview */}
          {activeTab === 'landing' && currentLandingPage?.landingPage && (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={s.previewToolbar}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#00395C' }}>Landing Page Preview</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button style={s.copyBtn} onClick={() => copyHtml(generateLandingPageHtml(currentLandingPage.landingPage), setCopiedLp)}>
                    {copiedLp ? '✓ Copied!' : 'Copy HTML'}
                  </button>
                  <button style={{ ...s.copyBtn, background: ddPushStatus === 'pushing' ? '#999' : '#28a745' }}
                    onClick={() => pushToDotdigital('landing')} disabled={ddPushStatus === 'pushing'}>
                    {ddPushStatus === 'pushing' ? 'Pushing...' : ddPushStatus === 'success' ? '✓ Pushed!' : 'Push to Dotdigital'}
                  </button>
                  <button style={{ ...s.copyBtn, background: '#00395C' }} onClick={() => setShowPublish(!showPublish)}>
                    Publish Live
                  </button>
                </div>
              </div>
              {showPublish && (
                <div style={{ background: '#f0f4ff', padding: '10px 16px', borderBottom: '1px solid #d0daf0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: '#00395C', fontWeight: 600 }}>welcome.crabplace.com /</span>
                  <input type="text" placeholder="slug (e.g. gift-card-25)" value={publishSlug}
                    onChange={e => setPublishSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    onKeyDown={e => { if (e.key === 'Enter') publishLandingPage(); }}
                    style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 12, fontFamily: 'monospace', width: 180 }} />
                  <button onClick={publishLandingPage}
                    disabled={!publishSlug.trim() || publishStatus === 'publishing'}
                    style={{ ...s.copyBtn, background: publishStatus === 'publishing' ? '#999' : publishStatus === 'success' ? '#28a745' : '#00395C', fontSize: 11 }}>
                    {publishStatus === 'publishing' ? 'Publishing...' : publishStatus === 'success' ? '✓ Live!' : 'Publish'}
                  </button>
                  {publishStatus === 'success' && publishMsg && (
                    <a href={publishMsg} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#28a745', fontWeight: 600 }}>{publishMsg}</a>
                  )}
                  {publishStatus === 'error' && publishMsg && (
                    <span style={{ fontSize: 11, color: '#dc3545' }}>Error: {publishMsg}</span>
                  )}
                </div>
              )}
              <div style={s.previewScroll}>
                <div ref={lpPreviewRef} style={s.lpFrame}>
                  <div style={{ background: '#00395C', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>🦀 CrabPlace.com</span>
                    <div style={{ display: 'flex', gap: 16 }}>
                      {['Shop', 'My Account', 'About'].map(l => <span key={l} style={{ color: '#FCD299', fontSize: 12 }}>{l}</span>)}
                    </div>
                  </div>
                  {/* Only show hardcoded hero if there are no sections (AI always includes its own hero section) */}
                  {!(currentLandingPage.landingPage.sections?.length > 0) && (
                    <div style={{ background: 'linear-gradient(135deg, #00395C, #002640)', padding: '44px 24px', textAlign: 'center' }}>
                      <Editable tag="h2" value={currentLandingPage.landingPage.headline} onChange={v => editLp('headline', v)}
                        style={{ color: '#fff', fontSize: 28, fontWeight: 800, lineHeight: 1.3, margin: '0 0 10px' }} />
                      {currentLandingPage.landingPage.subheadline && (
                        <Editable tag="p" value={currentLandingPage.landingPage.subheadline} onChange={v => editLp('subheadline', v)}
                          style={{ color: '#FCD299', fontSize: 14, marginBottom: 18 }} />
                      )}
                      {currentLandingPage.landingPage.ctaText && (
                        <Editable tag="a" value={currentLandingPage.landingPage.ctaText} onChange={v => editLp('ctaText', v)}
                          style={{ display: 'inline-block', padding: '12px 28px', background: '#DC582A', color: '#fff', borderRadius: 6, textDecoration: 'none', fontWeight: 700, fontSize: 14 }} />
                      )}
                    </div>
                  )}
                  <style>{`
                    .lp-section img { max-width: 100% !important; height: auto !important; display: block !important; margin-left: auto !important; margin-right: auto !important; float: none !important; }
                    .lp-section div, .lp-section p, .lp-section span { text-align: center !important; float: none !important; }
                    .lp-section > div { margin-left: auto !important; margin-right: auto !important; }
                  `}</style>
                  {currentLandingPage.landingPage.sections?.map((sec, i) => (
                    <Editable key={i} tag="div" value={sec.content} onChange={v => editLpSection(i, v)} isHtml className="lp-section"
                      style={{ padding: '24px 20px', background: i % 2 === 0 ? '#fff' : '#f8f9fa', fontSize: 13, lineHeight: 1.7, color: '#333', textAlign: 'center' }} />
                  ))}
                  <div style={{ background: '#00395C', padding: '18px 20px', textAlign: 'center' }}>
                    <p style={{ color: '#fff', fontSize: 14, fontWeight: 700, margin: '0 0 4px' }}>🦀 CrabPlace.com</p>
                    <p style={{ color: '#FCD299', fontSize: 10, margin: 0 }}>The First Online Seafood Retailer — Est. 1995</p>
                  </div>
                </div>
              </div>
              {currentLandingPage.thinking && <div style={s.thinking}><strong>Approach:</strong> {currentLandingPage.thinking}</div>}
            </div>
          )}

          {/* Brand Kit */}
          {activeTab === 'brand-kit' && (
            <div style={{ height: '100%', overflowY: 'auto', padding: 20, background: dragOver ? '#e8f4ff' : '#f0f2f5', transition: 'background 0.2s', position: 'relative' }}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={e => { e.preventDefault(); setDragOver(false); }}
              onDrop={handleBrandKitDrop}>
              {dragOver && (
                <div style={{ position: 'absolute', inset: 20, border: `3px dashed ${showImageGen ? '#7C3AED' : '#00395C'}`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: showImageGen ? 'rgba(124,58,237,0.06)' : 'rgba(0,57,92,0.06)', zIndex: 10, pointerEvents: 'none' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: showImageGen ? '#7C3AED' : '#00395C' }}>{showImageGen ? 'Drop image to edit with AI' : 'Drop images here to upload'}</div>
                </div>
              )}
              {uploading > 0 && (
                <div style={{ position: 'fixed', bottom: 20, right: 20, background: '#00395C', color: '#fff', padding: '10px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                  Uploading {uploading} image{uploading > 1 ? 's' : ''}...
                </div>
              )}
              <div style={{ maxWidth: 800, margin: '0 auto' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <div>
                    <h2 style={{ fontSize: 18, fontWeight: 800, color: '#00395C', margin: 0 }}>Brand Kit</h2>
                    <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0' }}>
                      Assets here are injected into every AI prompt — Claude will use real URLs and HTML instead of placeholders.
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => { setBulkPaste(true); setAddingAsset(null); setEditingAsset(null); setBulkCategory(folderPath.length > 0 ? folderPath.join('/') : ''); }}
                      style={{ padding: '6px 12px', background: '#EA733D', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      Bulk Paste
                    </button>
                    <button onClick={() => { setAddingAsset('image'); setEditingAsset(null); setBulkPaste(false); setAssetForm({ name: '', category: folderPath.length > 0 ? folderPath.join('/') : '', url: '', html: '' }); }}
                      style={{ padding: '6px 12px', background: '#00395C', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      + Image
                    </button>
                    <button onClick={() => { setAddingAsset('block'); setEditingAsset(null); setBulkPaste(false); setAssetForm({ name: '', category: folderPath.length > 0 ? folderPath.join('/') : '', url: '', html: '' }); }}
                      style={{ padding: '6px 12px', background: '#DC582A', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      + Building Block
                    </button>
                    <button onClick={() => { setShowImageGen(true); setBulkPaste(false); setAddingAsset(null); setEditingAsset(null); setImageFolder(folderPath.length > 0 ? folderPath.join('/') : ''); setImageHistory([]); setEditSourceAsset(null); setImagePrompt(''); setSelectedOverlays([]); }}
                      style={{ padding: '6px 12px', background: 'linear-gradient(135deg, #7C3AED, #DC582A)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      AI Generate
                    </button>
                  </div>
                </div>

                {/* AI Image Studio */}
                {showImageGen && (
                  <div style={{ background: '#fff', borderRadius: 8, marginBottom: 16, border: imageStudioDragOver ? '2px dashed #7C3AED' : '2px solid #7C3AED', overflow: 'hidden', position: 'relative' }}
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); setImageStudioDragOver(true); }}
                    onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setImageStudioDragOver(false); }}
                    onDrop={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setImageStudioDragOver(false);
                      const file = Array.from(e.dataTransfer.files).find(f => f.type.startsWith('image/'));
                      if (file) uploadFileForEdit(file);
                    }}>
                    {/* Header */}
                    <div style={{ background: 'linear-gradient(135deg, #7C3AED, #DC582A)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>AI Image Studio</div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <select value={imageSize} onChange={e => setImageSize(e.target.value)}
                          style={{ padding: '4px 8px', border: 'none', borderRadius: 4, fontSize: 10, fontFamily: 'inherit', background: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                          <option value="1024x1024" style={{ color: '#333' }}>Square</option>
                          <option value="1536x1024" style={{ color: '#333' }}>Landscape</option>
                          <option value="1024x1536" style={{ color: '#333' }}>Portrait</option>
                        </select>
                        <select value={imageFolder} onChange={e => setImageFolder(e.target.value)}
                          style={{ padding: '4px 8px', border: 'none', borderRadius: 4, fontSize: 10, fontFamily: 'inherit', background: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                          <option value="" style={{ color: '#333' }}>Save to...</option>
                          <option value="Generated" style={{ color: '#333' }}>Generated</option>
                          {[...new Set(brandAssets.filter(a => a.asset_type === 'image').map(a => a.category).filter(Boolean))].sort().map(cat => (
                            <option key={cat} value={cat} style={{ color: '#333' }}>{cat}</option>
                          ))}
                        </select>
                        <button onClick={() => { setShowImageGen(false); setImagePrompt(''); setImageHistory([]); setEditSourceAsset(null); }}
                          style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 4, color: '#fff', cursor: 'pointer', padding: '4px 8px', fontSize: 10, fontFamily: 'inherit' }}>Close</button>
                      </div>
                    </div>

                    {/* Drag overlay */}
                    {imageStudioDragOver && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(124,58,237,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, pointerEvents: 'none' }}>
                        <div style={{ padding: '16px 24px', background: '#fff', borderRadius: 8, border: '2px dashed #7C3AED', fontSize: 13, fontWeight: 600, color: '#7C3AED' }}>
                          Drop image to edit
                        </div>
                      </div>
                    )}

                    {/* Conversation area */}
                    <div ref={imageConvoRef} style={{ maxHeight: 350, overflowY: 'auto', padding: 12 }}>
                      {imageHistory.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '16px 10px' }}>
                          <div style={{ color: '#888', fontSize: 11, lineHeight: 1.6, marginBottom: 10 }}>
                            Describe the image you want, or click a suggestion below.<br />
                            After generating, keep chatting to refine: "make it brighter", "add steam", "zoom out".
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                            {[
                              'Steamed crabs with seasoning on a dock, waterfront lifestyle background',
                              'Maryland blue crab feast on a rustic wood table, overhead shot',
                              'Crab cakes on a white plate with lemon and Old Bay, clean product shot',
                              'Gift box of seafood with CrabPlace branding, holiday feel',
                              'Fresh lobster tails on ice with herbs, dark moody background',
                              'Family at a picnic table cracking crabs, warm summer evening',
                            ].map((suggestion, i) => (
                              <button key={i} onClick={() => setImagePrompt(suggestion)}
                                style={{ padding: '5px 10px', background: '#f5f3ff', border: '1px solid #ddd', borderRadius: 12, fontSize: 10, color: '#555', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', transition: 'border-color 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.borderColor = '#7C3AED'}
                                onMouseLeave={e => e.currentTarget.style.borderColor = '#ddd'}>
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {imageHistory.map((item, i) => (
                        <div key={i} style={{ marginBottom: 12 }}>
                          {/* Source preview for editing */}
                          {item.isSourcePreview ? (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6 }}>
                              <span style={{ fontSize: 14 }}>🎨</span>
                              <div>
                                <div style={{ fontSize: 10, color: '#7C3AED', fontWeight: 600, marginBottom: 4 }}>Editing: {editSourceAsset?.name || 'image'}</div>
                                <img src={item.url} alt="Source" onClick={() => setPreviewImage({ url: item.url, name: editSourceAsset?.name || 'Source image' })}
                                  style={{ maxWidth: 200, borderRadius: 6, border: '2px solid #7C3AED', display: 'block', cursor: 'pointer' }}
                                  title="Click to view full size"
                                  onError={e => { e.target.style.display = 'none'; }} />
                                {/* Edit suggestion tags — show when no edits applied yet */}
                                {imageHistory.filter(h => !h.isSourcePreview).length === 0 && (
                                  <div style={{ marginTop: 6 }}>
                                    <div style={{ fontSize: 9, color: '#999', marginBottom: 3, fontWeight: 600 }}>INSTANT OVERLAYS — select one or more, then Apply</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6, alignItems: 'center' }}>
                                      {[
                                        { label: '"Our Boxes Make People Happy"', id: 'boxes-happy' },
                                        { label: '"Free Shipping"', id: 'free-shipping' },
                                        { label: 'Sale badge', id: 'sale-percent' },
                                        { label: 'Logo', id: 'logo' },
                                      ].map((tag, j) => {
                                        const selected = selectedOverlays.includes(tag.id);
                                        return (
                                          <button key={j} onClick={() => setSelectedOverlays(prev => selected ? prev.filter(t => t !== tag.id) : [...prev, tag.id])} disabled={imageGenerating}
                                            style={{ padding: '3px 8px', background: selected ? '#2e7d32' : '#e8f5e9', border: `1px solid ${selected ? '#2e7d32' : '#a5d6a7'}`, borderRadius: 10, fontSize: 9, color: selected ? '#fff' : '#2e7d32', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, transition: 'all 0.15s' }}>
                                            {selected ? '✓ ' : ''}{tag.label}
                                          </button>
                                        );
                                      })}
                                      {selectedOverlays.length > 0 && (
                                        <button onClick={() => canvasOverlay(selectedOverlays)} disabled={imageGenerating}
                                          style={{ padding: '4px 12px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 10, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                          Apply{selectedOverlays.length > 1 ? ` (${selectedOverlays.length})` : ''}
                                        </button>
                                      )}
                                    </div>
                                    <div style={{ fontSize: 9, color: '#999', marginBottom: 3, fontWeight: 600 }}>AI EDITS</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                      {[
                                        { label: 'Remove background', prompt: 'Remove the background and make it transparent/white. Keep the subject only.' },
                                        { label: 'Make brighter', prompt: 'Make the image brighter and more vibrant. Keep everything else the same.' },
                                        { label: 'Add holiday border', prompt: 'Add a festive holiday-themed border around the image. Do not alter the photo inside.' },
                                      ].map((tag, j) => (
                                        <button key={j} onClick={() => setImagePrompt(tag.prompt)}
                                          style={{ padding: '3px 8px', background: '#f5f3ff', border: '1px solid #ddd', borderRadius: 10, fontSize: 9, color: '#555', cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.15s' }}
                                          onMouseEnter={e => e.currentTarget.style.borderColor = '#7C3AED'}
                                          onMouseLeave={e => e.currentTarget.style.borderColor = '#ddd'}>
                                          {tag.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (<>
                          {/* Prompt */}
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                            <div style={{ background: '#7C3AED', color: '#fff', padding: '6px 10px', borderRadius: '10px 10px 4px 10px', maxWidth: '80%', fontSize: 11 }}>
                              {item.prompt}
                            </div>
                          </div>
                          {/* Result */}
                          {item.assistantText ? (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                              <span style={{ fontSize: 14 }}>🎨</span>
                              <div style={{ background: '#f5f3ff', padding: '8px 10px', borderRadius: '10px 10px 10px 4px', fontSize: 11, color: '#555', maxWidth: '80%', lineHeight: 1.5 }}>
                                {item.assistantText}
                              </div>
                            </div>
                          ) : item.error ? (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                              <span style={{ fontSize: 14 }}>🎨</span>
                              <div style={{ background: '#FFF0F0', padding: '6px 10px', borderRadius: '10px 10px 10px 4px', fontSize: 11, color: '#dc3545' }}>
                                Error: {item.error}
                              </div>
                            </div>
                          ) : item.url ? (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                              <span style={{ fontSize: 14 }}>🎨</span>
                              <div>
                                <img src={item.url} alt={item.prompt} onClick={() => setPreviewImage({ url: item.url, name: item.prompt })}
                                  style={{ maxWidth: 280, borderRadius: 6, border: '1px solid #e5e7eb', display: 'block', cursor: 'pointer' }}
                                  title="Click to view full size"
                                  onError={e => { e.target.style.display = 'none'; }} />
                                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                  <button onClick={() => saveImageToBrandKit(item)}
                                    style={{ padding: '4px 10px', background: '#00395C', color: '#fff', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                                    Save to Brand Kit
                                  </button>
                                  {i === imageHistory.length - 1 && (
                                    <span style={{ fontSize: 10, color: '#888', alignSelf: 'center' }}>Type below to refine this image</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          </>)}
                        </div>
                      ))}
                      {imageGenerating && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 14 }}>🎨</span>
                          <div style={{ background: '#f5f3ff', padding: '8px 12px', borderRadius: '10px 10px 10px 4px', fontSize: 11, color: '#7C3AED' }}>
                            {editSourceAsset || imageHistory.some(h => !h.isSourcePreview) ? 'Editing image... 30-60 seconds' : 'Generating image... 10-20 seconds'}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Pasted image preview */}
                    {editSourceAsset?.pasted && imageHistory.length > 0 && imageHistory[0]?.isSourcePreview && !imageHistory.some(h => !h.isSourcePreview) && (
                      <div style={{ borderTop: '1px solid #e5e7eb', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, background: '#f5f3ff' }}>
                        <img src={editSourceAsset.url} alt="Pasted" style={{ width: 36, height: 36, borderRadius: 4, objectFit: 'cover', border: '1px solid #7C3AED' }} />
                        <span style={{ fontSize: 10, color: '#7C3AED', fontWeight: 600, flex: 1 }}>Pasted image ready to edit</span>
                        <button onClick={() => { setEditSourceAsset(null); setImageHistory([]); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#999', padding: 2 }}>✕</button>
                      </div>
                    )}

                    {/* Input */}
                    <div style={{ borderTop: '1px solid #e5e7eb', padding: '8px 12px', display: 'flex', gap: 6 }}>
                      <input type="text" placeholder={editSourceAsset || imageHistory.some(h => !h.isSourcePreview) ? 'Type text to overlay, or describe an AI edit...' : 'Describe the image you want, or paste an image...'}
                        value={imagePrompt} onChange={e => setImagePrompt(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && imagePrompt.trim() && !imageGenerating) { if (editSourceAsset || imageHistory.length > 0) generateImage(); } }}
                        onPaste={e => {
                          const items = e.clipboardData?.items;
                          if (!items) return;
                          const imageItem = Array.from(items).find(item => item.type.startsWith('image/'));
                          if (!imageItem) return;
                          e.preventDefault();
                          const file = imageItem.getAsFile();
                          if (file) uploadFileForEdit(file);
                        }}
                        disabled={imageGenerating}
                        style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
                      {/* Text overlay button — shown when editing an image and there's text typed */}
                      {(editSourceAsset || imageHistory.some(h => !h.isSourcePreview)) && imagePrompt.trim() && (
                        <button onClick={() => canvasOverlay('custom-text')} disabled={imageGenerating}
                          title="Add as text overlay (instant)"
                          style={{ padding: '8px 10px', background: imageGenerating ? '#ccc' : '#2e7d32', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: imageGenerating ? 'default' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                          Aa
                        </button>
                      )}
                      <button onClick={generateImage} disabled={imageGenerating || !imagePrompt.trim()}
                        style={{ padding: '8px 14px', background: !imagePrompt.trim() || imageGenerating ? '#ccc' : '#7C3AED', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: !imagePrompt.trim() || imageGenerating ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                        {imageGenerating ? '...' : imageHistory.length === 0 && !editSourceAsset ? 'Generate' : 'AI Edit'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Bulk Paste */}
                {bulkPaste && (
                  <div style={{ background: '#fff', borderRadius: 8, padding: 16, marginBottom: 16, border: '2px solid #EA733D' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#00395C', marginBottom: 4 }}>Bulk Image Import</div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 10, lineHeight: 1.5 }}>
                      Pick a folder, then paste image URLs one at a time. Each paste auto-imports instantly.
                    </div>
                    <select value={bulkCategory} onChange={e => setBulkCategory(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', marginBottom: 8, boxSizing: 'border-box', background: '#fff' }}>
                      <option value="">Select folder...</option>
                      {[...new Set(brandAssets.filter(a => a.asset_type === 'image').map(a => a.category || 'Uncategorized'))].sort().map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <input type="text" placeholder="Paste a URL — it auto-imports instantly" value={bulkText}
                      onChange={e => setBulkText(e.target.value)}
                      onPaste={e => {
                        e.preventDefault();
                        const pasted = e.clipboardData.getData('text').trim();
                        if (!pasted) return;
                        setBulkText(pasted);
                        // Auto-import after a tick so state updates
                        setTimeout(async () => {
                          const lines = pasted.split('\n').map(l => l.trim()).filter(Boolean);
                          setBulkImporting(true);
                          let latestRows = brandAssets;
                          for (const line of lines) {
                            let name, url;
                            if (line.includes('|')) {
                              const parts = line.split('|').map(p => p.trim());
                              name = parts[0]; url = parts[1];
                            } else {
                              url = line;
                              try { name = decodeURIComponent(new URL(url).pathname.split('/').pop().replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')); } catch { name = url.slice(-40); }
                            }
                            if (!url.startsWith('http')) continue;
                            try { latestRows = await neonQuery('brand_assets_add', { asset_type: 'image', name, category: bulkCategory.trim() || null, url }); } catch (e) { console.error(e); }
                          }
                          setBrandAssets(latestRows);
                          setBulkImporting(false);
                          setBulkText('');
                        }, 0);
                      }}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', boxSizing: 'border-box', marginBottom: 8 }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: bulkImporting ? '#DC582A' : '#888' }}>
                        {bulkImporting ? 'Importing...' : 'Paste a URL — it imports instantly and clears for the next one'}
                      </span>
                      <button onClick={() => { setBulkPaste(false); setBulkText(''); setBulkCategory(''); }}
                        style={{ padding: '6px 14px', background: '#f0f0f0', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>Done</button>
                    </div>
                  </div>
                )}

                {/* Add/Edit Form */}
                {(addingAsset || editingAsset) && (
                  <div style={{ background: '#fff', borderRadius: 8, padding: 16, marginBottom: 16, border: '2px solid #DC582A' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#00395C', marginBottom: 12 }}>
                      {editingAsset ? `Edit ${editingAsset.asset_type === 'image' ? 'Image' : 'Building Block'}` : `Add ${addingAsset === 'image' ? 'Image' : 'Building Block'}`}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input type="text" placeholder="Name (e.g., Logo, Header, Footer)" value={assetForm.name}
                        onChange={e => setAssetForm(p => ({ ...p, name: e.target.value }))}
                        style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit' }} />
                      <select value={assetForm.category} onChange={e => setAssetForm(p => ({ ...p, category: e.target.value }))}
                        style={{ width: 200, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', background: '#fff' }}>
                        <option value="">No folder</option>
                        {[...new Set(brandAssets.map(a => a.category).filter(Boolean))].sort().map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    {(addingAsset === 'image' || editingAsset?.asset_type === 'image') && (
                      <input type="text" placeholder="Image URL (full https:// URL)" value={assetForm.url}
                        onChange={e => setAssetForm(p => ({ ...p, url: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', marginBottom: 8, boxSizing: 'border-box' }} />
                    )}
                    {(addingAsset === 'block' || editingAsset?.asset_type === 'block') && (
                      <textarea placeholder="Paste the HTML for this building block..." value={assetForm.html}
                        onChange={e => setAssetForm(p => ({ ...p, html: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 11, fontFamily: 'monospace', minHeight: 120, resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }} />
                    )}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button onClick={() => { setAddingAsset(null); setEditingAsset(null); setAssetForm({ name: '', category: '', url: '', html: '' }); }}
                        style={{ padding: '6px 14px', background: '#f0f0f0', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                      <button onClick={editingAsset ? updateBrandAsset : addBrandAsset} disabled={!assetForm.name.trim()}
                        style={{ padding: '6px 14px', background: assetForm.name.trim() ? '#00395C' : '#ccc', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: assetForm.name.trim() ? 'pointer' : 'default', fontFamily: 'inherit' }}>
                        {editingAsset ? 'Save Changes' : 'Add Asset'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Folder / Grid view */}
                {(() => {
                  if (brandLoading) return <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 12 }}>Loading brand assets...</div>;
                  if (brandAssets.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 12, background: '#fff', borderRadius: 8, border: '1px dashed #ddd' }}>No assets yet. Add images or building blocks to get started.</div>;

                  const currentPrefix = folderPath.join('/');
                  // Get items whose category starts with the current path
                  const allImages = brandAssets.filter(a => a.asset_type === 'image');
                  const allBlocks = brandAssets.filter(a => a.asset_type === 'block');

                  // For current level: find subfolders and direct items
                  const getChildren = (items, prefix) => {
                    const directItems = []; // items at exactly this level
                    const subfolderSet = new Set(); // immediate child folder names
                    items.forEach(a => {
                      const cat = a.category || 'Uncategorized';
                      if (prefix === '') {
                        // Root level — every top-level category becomes a folder
                        const parts = cat.split('/');
                        subfolderSet.add(parts[0]);
                      } else if (cat.startsWith(prefix + '/')) {
                        // Has deeper nesting — next segment becomes a subfolder
                        const rest = cat.slice(prefix.length + 1);
                        const parts = rest.split('/');
                        if (parts.length === 1) directItems.push(a);
                        else subfolderSet.add(parts[0]);
                      } else if (cat === prefix) {
                        // Exact match — direct item in this folder
                        directItems.push(a);
                      }
                    });
                    // Count items in each subfolder (recursively), excluding .folder placeholders
                    const subfolders = [...subfolderSet].sort().map(name => {
                      const subPrefix = prefix ? prefix + '/' + name : name;
                      const count = items.filter(a => (a.category || 'Uncategorized').startsWith(subPrefix) && a.name !== '.folder').length;
                      const preview = items.find(a => (a.category || 'Uncategorized').startsWith(subPrefix) && a.url && a.name !== '.folder');
                      return { name, count, preview, path: subPrefix };
                    });
                    return { directItems: directItems.filter(a => a.name !== '.folder'), subfolders };
                  };

                  const imgChildren = getChildren(allImages, currentPrefix);
                  const blkChildren = getChildren(allBlocks, currentPrefix);
                  const hasSubfolders = imgChildren.subfolders.length > 0 || blkChildren.subfolders.length > 0;
                  const hasDirectImages = imgChildren.directItems.length > 0;
                  const hasDirectBlocks = blkChildren.directItems.length > 0;

                  return (
                    <div>
                      {/* Breadcrumb + New Folder */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, minHeight: 24 }}>
                        {folderPath.length > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, flexWrap: 'wrap' }}>
                            <button onClick={() => setFolderPath([])}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, color: '#00395C', padding: 0, fontFamily: 'inherit', fontSize: 12 }}>
                              All
                            </button>
                            {folderPath.map((seg, i) => (
                              <React.Fragment key={i}>
                                <ChevronRight size={12} color="#ccc" />
                                <button onClick={() => setFolderPath(folderPath.slice(0, i + 1))}
                                  style={{ background: 'none', border: 'none', cursor: i < folderPath.length - 1 ? 'pointer' : 'default', fontWeight: i === folderPath.length - 1 ? 700 : 600, color: '#00395C', padding: 0, fontFamily: 'inherit', fontSize: 12 }}>
                                  {seg}
                                </button>
                              </React.Fragment>
                            ))}
                          </div>
                        ) : <div />}
                        {!creatingFolder && (
                          <button onClick={() => { setCreatingFolder(true); setNewFolderName(''); }}
                            style={{ padding: '4px 10px', background: '#f0f0f0', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: '#00395C' }}>
                            + New Folder
                          </button>
                        )}
                      </div>

                      {/* Create folder inline */}
                      {creatingFolder && (
                        <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center' }}>
                          <FolderOpen size={16} color="#00395C" />
                          <input type="text" autoFocus placeholder="Folder name" value={newFolderName}
                            onChange={e => setNewFolderName(e.target.value)}
                            onKeyDown={async e => {
                              if (e.key === 'Enter' && newFolderName.trim()) {
                                const fullPath = folderPath.length > 0 ? folderPath.join('/') + '/' + newFolderName.trim() : newFolderName.trim();
                                try {
                                  const rows = await neonQuery('brand_assets_add', { asset_type: 'image', name: '.folder', category: fullPath, url: null });
                                  setBrandAssets(rows);
                                } catch (err) { console.error('Failed to create folder:', err); }
                                setFolderPath(fullPath.split('/'));
                                setCreatingFolder(false);
                                setNewFolderName('');
                              }
                              if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); }
                            }}
                            style={{ flex: 1, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit' }} />
                          <button onClick={async () => {
                            if (!newFolderName.trim()) return;
                            const fullPath = folderPath.length > 0 ? folderPath.join('/') + '/' + newFolderName.trim() : newFolderName.trim();
                            try {
                              const rows = await neonQuery('brand_assets_add', { asset_type: 'image', name: '.folder', category: fullPath, url: null });
                              setBrandAssets(rows);
                            } catch (err) { console.error('Failed to create folder:', err); }
                            setFolderPath(fullPath.split('/'));
                            setCreatingFolder(false);
                            setNewFolderName('');
                          }}
                            style={{ padding: '6px 12px', background: newFolderName.trim() ? '#00395C' : '#ccc', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: newFolderName.trim() ? 'pointer' : 'default', fontFamily: 'inherit' }}>
                            Create
                          </button>
                          <button onClick={() => { setCreatingFolder(false); setNewFolderName(''); }}
                            style={{ padding: '6px 10px', background: '#f0f0f0', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                        </div>
                      )}

                      {/* Subfolders — images */}
                      {imgChildren.subfolders.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          {folderPath.length === 0 && <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Images</div>}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {imgChildren.subfolders.map(sf => (
                              <div key={`img-${sf.path}`} onClick={() => setFolderPath(sf.path.split('/'))}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb', cursor: 'pointer', transition: 'border-color 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.borderColor = '#DC582A'}
                                onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}>
                                {sf.preview?.url ? (
                                  <img src={sf.preview.url} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, background: '#f8f9fa' }} onError={e => { e.target.style.display = 'none'; }} />
                                ) : (
                                  <FolderOpen size={20} color="#00395C" />
                                )}
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: '#00395C' }}>{sf.name}</div>
                                  <div style={{ fontSize: 10, color: '#888' }}>{sf.count} image{sf.count !== 1 ? 's' : ''}</div>
                                </div>
                                <ChevronRight size={16} color="#ccc" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Subfolders — blocks */}
                      {blkChildren.subfolders.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          {folderPath.length === 0 && <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Building Blocks</div>}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {blkChildren.subfolders.map(sf => (
                              <div key={`blk-${sf.path}`} onClick={() => setFolderPath(sf.path.split('/'))}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb', cursor: 'pointer', transition: 'border-color 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.borderColor = '#DC582A'}
                                onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}>
                                <FolderOpen size={20} color="#DC582A" />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: '#00395C' }}>{sf.name}</div>
                                  <div style={{ fontSize: 10, color: '#888' }}>{sf.count} block{sf.count !== 1 ? 's' : ''}</div>
                                </div>
                                <ChevronRight size={16} color="#ccc" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Direct images at this level — thumbnail grid */}
                      {hasDirectImages && (
                        <div style={{ marginBottom: 16 }}>
                          {hasSubfolders && <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Images</div>}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
                            {imgChildren.directItems.map(asset => (
                              <div key={asset.id} style={{ background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                                <div onClick={() => asset.url && setPreviewImage({ url: asset.url, name: asset.name })}
                                  style={{ width: '100%', paddingTop: '100%', position: 'relative', background: '#f8f9fa', cursor: asset.url ? 'pointer' : 'default' }}>
                                  {asset.url && (
                                    <img src={asset.url} alt={asset.name} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                      onError={e => { e.target.style.display = 'none'; }} />
                                  )}
                                </div>
                                <div style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <div style={{ fontSize: 10, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={asset.name}>{asset.name}</div>
                                  <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                                    <button onClick={() => startEditExistingImage(asset)} title="AI Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                      <Wand2 size={12} color="#7C3AED" />
                                    </button>
                                    <button onClick={() => startEdit(asset)} title="Edit details" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                      <Pencil size={12} color="#00395C" />
                                    </button>
                                    <button onClick={() => removeBrandAsset(asset.id)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                      <Trash2 size={12} color="#dc3545" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Direct blocks at this level */}
                      {hasDirectBlocks && (
                        <div style={{ marginBottom: 16 }}>
                          {hasSubfolders && <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Building Blocks</div>}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {blkChildren.directItems.map(asset => (
                              <div key={asset.id} style={{ background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
                                  <div style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>{asset.name}</div>
                                  <button onClick={() => startEdit(asset)} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                    <Pencil size={13} color="#00395C" />
                                  </button>
                                  <button onClick={() => removeBrandAsset(asset.id)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                    <Trash2 size={13} color="#dc3545" />
                                  </button>
                                </div>
                                {asset.html && (
                                  <div style={{ padding: '8px 12px', background: '#f8f9fa', borderTop: '1px solid #e5e7eb', fontSize: 10, fontFamily: 'monospace', color: '#555', maxHeight: 80, overflow: 'hidden', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                    {asset.html.slice(0, 300)}{asset.html.length > 300 ? '...' : ''}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!hasSubfolders && !hasDirectImages && !hasDirectBlocks && (
                        <div style={{ padding: 32, textAlign: 'center', color: '#999', fontSize: 12, background: '#fff', borderRadius: 8, border: '2px dashed #ddd' }}>
                          <div style={{ fontSize: 28, marginBottom: 8 }}>📁</div>
                          <div>This folder is empty.</div>
                          <div style={{ marginTop: 4, fontSize: 11 }}>Drag & drop images here to upload</div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Knowledge (Strategies + Style Prefs) */}
          {activeTab === 'strategies' && (
            <div style={{ height: '100%', overflowY: 'auto', padding: 20, background: '#f0f2f5' }}>
              <div style={{ maxWidth: 800, margin: '0 auto' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <div>
                    <h2 style={{ fontSize: 18, fontWeight: 800, color: '#00395C', margin: 0 }}>Knowledge Base</h2>
                    <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0' }}>
                      Strategies and preferences are injected into every AI prompt — Claude remembers your context.
                    </p>
                  </div>
                  <button onClick={() => { setAddingStrategy(true); setEditingStrategy(null); setStrategyForm({ category: 'General', title: '', detail: '' }); }}
                    style={{ padding: '6px 12px', background: '#00395C', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    + Add Strategy
                  </button>
                </div>

                {/* Add/Edit Strategy Form */}
                {(addingStrategy || editingStrategy) && (
                  <div style={{ background: '#fff', borderRadius: 8, padding: 16, marginBottom: 16, border: '2px solid #DC582A' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#00395C', marginBottom: 10 }}>
                      {editingStrategy ? 'Edit Strategy' : 'New Strategy'}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <select value={strategyForm.category} onChange={e => setStrategyForm(p => ({ ...p, category: e.target.value }))}
                        style={{ width: 160, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', background: '#fff' }}>
                        {[...new Set(['General', 'Email Strategy', 'Brand', 'Campaigns', 'Audience', 'Seasonal', ...strategies.map(s => s.category)])].map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                      <input type="text" placeholder="Strategy or context note" value={strategyForm.title}
                        onChange={e => setStrategyForm(p => ({ ...p, title: e.target.value }))}
                        style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit' }} />
                    </div>
                    <textarea placeholder="Details, reasoning, or additional context (optional)" value={strategyForm.detail}
                      onChange={e => setStrategyForm(p => ({ ...p, detail: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', minHeight: 60, resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }} />
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button onClick={() => { setAddingStrategy(false); setEditingStrategy(null); }}
                        style={{ padding: '6px 14px', background: '#f0f0f0', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                      <button onClick={editingStrategy ? updateStrategy : addStrategy} disabled={!strategyForm.title.trim()}
                        style={{ padding: '6px 14px', background: strategyForm.title.trim() ? '#00395C' : '#ccc', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: strategyForm.title.trim() ? 'pointer' : 'default', fontFamily: 'inherit' }}>
                        {editingStrategy ? 'Save Changes' : 'Add'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Strategies list */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#00395C', marginBottom: 8 }}>Strategies & Context</div>
                  {strategiesLoading ? (
                    <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 12 }}>Loading...</div>
                  ) : strategies.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#999', fontSize: 12, background: '#fff', borderRadius: 8, border: '1px dashed #ddd' }}>
                      No strategies yet. Add notes about your marketing approach, target audiences, current campaigns, etc.
                    </div>
                  ) : (
                    <div>
                      {[...new Set(strategies.map(s => s.category))].map(cat => (
                        <div key={cat} style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#DC582A', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{cat}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {strategies.filter(s => s.category === cat).map(strat => (
                              <div key={strat.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{strat.title}</div>
                                  {strat.detail && <div style={{ fontSize: 11, color: '#666', marginTop: 3, lineHeight: 1.5 }}>{strat.detail}</div>}
                                </div>
                                <button onClick={() => { setEditingStrategy(strat); setAddingStrategy(false); setStrategyForm({ category: strat.category, title: strat.title, detail: strat.detail || '' }); }}
                                  title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', flexShrink: 0 }}>
                                  <Pencil size={12} color="#00395C" />
                                </button>
                                <button onClick={() => removeStrategy(strat.id)} title="Remove"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', flexShrink: 0 }}>
                                  <Trash2 size={12} color="#dc3545" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Style Preferences */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#00395C', marginBottom: 8 }}>
                    Style Preferences
                    <span style={{ fontSize: 10, fontWeight: 500, color: '#888', marginLeft: 6 }}>— learned from your feedback</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    <input type="text" placeholder="Add a style preference (e.g., 'Keep headlines under 8 words')" value={newPref}
                      onChange={e => setNewPref(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && newPref.trim()) { addStylePref(newPref); setNewPref(''); } }}
                      style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit' }} />
                    <button onClick={() => { if (newPref.trim()) { addStylePref(newPref); setNewPref(''); } }} disabled={!newPref.trim()}
                      style={{ padding: '6px 14px', background: newPref.trim() ? '#00395C' : '#ccc', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: newPref.trim() ? 'pointer' : 'default', fontFamily: 'inherit' }}>
                      Add
                    </button>
                  </div>
                  {stylePrefs.length === 0 ? (
                    <div style={{ padding: 16, textAlign: 'center', color: '#999', fontSize: 12, background: '#fff', borderRadius: 8, border: '1px dashed #ddd' }}>
                      No style preferences yet. Add rules about tone, length, formatting, or anything Claude should always follow.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {stylePrefs.map(pref => (
                        <div key={pref.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', fontSize: 11 }}>
                          <span>{pref.pref}</span>
                          <button onClick={() => removeStylePref(pref.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
                            <Trash2 size={10} color="#dc3545" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div style={{ background: '#FFF8F5', border: '1px solid #FCD299', borderRadius: 8, padding: 14, fontSize: 11, color: '#666', lineHeight: 1.6 }}>
                  <strong style={{ color: '#00395C' }}>How it works:</strong> Everything here is injected into Claude's system prompt on every message.
                  Strategies give Claude context about your business decisions, campaigns, and goals.
                  Style preferences teach Claude your content rules — it'll apply them to everything it generates.
                  Conversations auto-save and can be resumed from the History button in the chat panel.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────
const s = {
  wrapper: { display: 'flex', height: 'calc(100vh - 57px)', overflow: 'hidden', background: '#f0f2f5', margin: -20, marginTop: -20 },
  // Chat
  chat: { width: 360, minWidth: 360, display: 'flex', flexDirection: 'column', background: '#fff', borderRight: '1px solid #e5e7eb' },
  chatHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#00395C', borderBottom: '1px solid #002640' },
  chatMessages: { flex: 1, overflowY: 'auto', padding: '12px 12px 4px' },
  promptChip: { padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 12, textAlign: 'left', marginBottom: 6, transition: 'all 0.15s' },
  userRow: { display: 'flex', justifyContent: 'flex-end', marginBottom: 10 },
  assistRow: { display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 10 },
  userBubble: { background: '#00395C', color: '#fff', padding: '8px 12px', borderRadius: '12px 12px 4px 12px', maxWidth: '80%', fontSize: 12, lineHeight: 1.5 },
  assistBubble: { background: '#f5f5f5', color: '#1a1a1a', padding: '8px 12px', borderRadius: '12px 12px 12px 4px', maxWidth: '90%', fontSize: 12, lineHeight: 1.5 },
  viewBtn: { padding: '4px 10px', background: '#DC582A', color: '#fff', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer' },
  inputBar: { padding: '10px 12px', borderTop: '1px solid #e5e7eb' },
  inputWrap: { display: 'flex', flexDirection: 'column', border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', background: '#fff' },
  textarea: { width: '100%', padding: '10px 12px', border: 'none', fontSize: 12, fontFamily: 'inherit', outline: 'none', resize: 'none', minHeight: 60, maxHeight: 150, lineHeight: 1.5 },
  inputActions: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, padding: '4px 8px', borderTop: '1px solid #f0f0f0' },
  micBtn: { border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 16, padding: '4px 8px', transition: 'all 0.15s' },
  sendBtn: { padding: '6px 14px', background: '#DC582A', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  // Preview
  preview: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  tabs: { display: 'flex', background: '#fff', borderBottom: '2px solid #e5e7eb', paddingLeft: 16 },
  tab: { padding: '10px 16px', border: 'none', background: 'transparent', fontSize: 12, fontWeight: 600, color: '#999', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive: { padding: '10px 16px', border: 'none', background: 'transparent', fontSize: 12, fontWeight: 600, color: '#00395C', cursor: 'pointer', borderBottom: '2px solid #DC582A', marginBottom: -2 },
  tabContent: { flex: 1, overflow: 'hidden' },
  // Getting Started
  startPanel: { height: '100%', overflowY: 'auto', padding: 20, background: '#f0f2f5' },
  startCard: { maxWidth: 600, margin: '0 auto', background: '#fff', borderRadius: 10, padding: '32px 28px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  capCard: { display: 'flex', gap: 10, padding: 12, background: '#f8f9fa', borderRadius: 8 },
  // Preview shared
  previewToolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #e5e7eb', background: '#fff' },
  copyBtn: { padding: '6px 14px', background: '#DC582A', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  previewScroll: { flex: 1, overflowY: 'auto', padding: 16, background: '#f0f2f5' },
  emailFrame: { maxWidth: 560, margin: '0 auto', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
  lpFrame: { maxWidth: 860, margin: '0 auto', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
  thinking: { padding: '8px 16px', background: '#FFF8F5', borderTop: '1px solid #FCD299', fontSize: 11, color: '#666' },
};
